<script setup>
    import { onMounted, onUnmounted, ref, watch, computed, nextTick } from 'vue'
    import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
    import {
        faClose,
        faUser,
        faCheck,
        faArrowRight,
        faShare,
        faCopy,
    } from '@fortawesome/free-solid-svg-icons'
    import { faCommentDots } from '@fortawesome/free-regular-svg-icons'
    import Scrollable from '@/Components/ui-components/Scrollable.vue'
    import AutoFocusInput from '@/Components/ui-components/AutoFocusInput.vue'
    import SvgSpinLoader from '@/Components/ui-components/SvgSpinLoader.vue'
    import LaravelErrorMessage from '@/Components/LaravelErrorMessage.vue'
    import { router } from '@inertiajs/vue3'
    import { usePage } from '@inertiajs/vue3'
    import SendToModelModal from '@/Components/Fantasy/Modals/SendToModel.vue'
    import { faPaperPlaneTop } from '@fortawesome/pro-light-svg-icons'
    import PlatformSubscriptionModal from '@/Components/PlatformSubscriptionModal.vue'
    import TokenPurchaseModal from '@/Components/Modals/TokenPurchaseModal.vue'
    import { useAuthReloader } from '@/composables/useAuthReloader'

    const page = usePage()
    // Only trigger auth reloader if user is logged in
    if (page?.props?.auth?.user) {
        useAuthReloader()
    }
    // Ensure user is always a valid computed ref - defensive check to prevent undefined errors
    const user = computed(() => {
        try {
            return page?.props?.auth?.user ?? null
        } catch (error) {
            console.warn('Error accessing user from page props:', error)
            return null
        }
    })

    const props = defineProps({
        isOpen: { type: Boolean, default: false },
        model: { type: Object, default: undefined },
        sessionId: { type: String, default: null },
        primaryVoice: { type: Object, default: null },
    })
    console.log(
        'primaryVoice Create modal - props.primaryVoice:',
        props.primaryVoice,
        'type:',
        typeof props.primaryVoice,
        'truthy:',
        !!props.primaryVoice,
    )
    console.log(
        'primaryVoice Create modal - lander primary voice:',
        usePage().props.lander?.primary_voice,
    )

    const sessionId = ref(props.sessionId || page.props.session || null)
    const landerId = ref(page.props.lander ? page.props.lander.id : null)
    const subscriptionModalVisible = ref(false)

    const userHasSubscription = computed(() => user.value?.has_subscription)
    const remainingFantasyCreations = computed(() => user.value?.remaining_fantasies)
    const fantasyCreationPrice = computed(() => page.props?.fantasy_creation_price || 0) // we check backend before purchase too.
    const userBalance = computed(() => user.value?.balance_tokens)

    const modelDisplayName = computed(() => {
        return selectedModel.value?.name || selectedModel.value?.scenario_name || 'this creator'
    })

    const selectedVoiceLabel = computed(() => {
        if (!selectedModel.value) return 'Select a voice'

        // Use same logic as Chat.vue for consistency
        const vendorName = selectedModel.value?.voice_vendor_name || null

        return vendorName ? `${vendorName}` : `${modelDisplayName.value}`
    })

    // Check if title and description are filled (for Subscribe button)
    const isTitleAndDescriptionFilled = computed(() => {
        return (
            scenarioName.value &&
            scenarioName.value.length >= 3 &&
            description.value &&
            description.value.length >= 30
        )
    })

    const openSubscriptionModal = () => {
        subscriptionModalVisible.value = true
    }

    const emit = defineEmits(['close'])

    const resetModal = () => {
        // Reset all form data
        currentStep.value = 1
        selectedCharacteristics.value = {}
        customCharacteristics.value = {}
        isOtherInputVisible.value = {}
        description.value = ''
        scenarioName.value = ''
        name.value = ''
        gender.value = 'female'
        selectedModelId.value = landerId.value || null
        selectedModel.value = props.model || null
        exitMessageVisible.value = false
        isLoading.value = false
        errors.value = null
        customErrors.value = {}
        ageErrors.value = {}
        validWords.value = []
        notValidWords.value = []
        redirectUrl.value = null
        createdFantasy.value = null
        showSendToModelModal.value = false
        showShareModal.value = false
        isSubmittingSendToCreator.value = false
        copied.value = false
        isSentSuccess.value = false
        showTooltip.value = false

        // Clear localStorage session data
        if (sessionId.value) {
            localStorage.removeItem('fantasy-session' + sessionId.value)
        }
    }

    const handleClose = () => {
        resetModal()
        emit('close')
    }

    const handleSendToCreator = () => {
        window.fathom && window.fathom.trackEvent('FantasyCreate-success-dm-creator')
        showSendToModelModal.value = true
    }

    const handleSendSuccess = () => {
        showSendToModelModal.value = false
        isSentSuccess.value = true
        // Redirect to home after 3 seconds
        setTimeout(() => {
            window.location.href = '/'
        }, 3000)
    }

    const handleStartChat = () => {
        window.fathom && window.fathom.trackEvent('FantasyCreate-success-start-chat')
        if (redirectUrl.value) {
            window.location.href = redirectUrl.value
        }
    }

    const handleShare = () => {
        window.fathom && window.fathom.trackEvent('FantasyCreate-success-share')
        showShareModal.value = true
    }

    const copyToClipboard = async () => {
        if (redirectUrl.value) {
            try {
                await navigator.clipboard.writeText(redirectUrl.value)
                copied.value = true
                setTimeout(() => {
                    copied.value = false
                }, 2000)
            } catch (err) {
                console.error('Failed to copy:', err)
            }
        }
    }

    const shareOnTwitter = () => {
        if (redirectUrl.value) {
            const text = encodeURIComponent(
                `Check out my fantasy creation with ${
                    selectedModel.value?.name || 'this creator'
                }!`,
            )
            const url = encodeURIComponent(redirectUrl.value)
            window.open(`https://twitter.com/intent/tweet?text=${text}&url=${url}`, '_blank')
        }
    }

    const shareViaSMS = () => {
        if (redirectUrl.value) {
            const text = `Check out my fantasy creation: ${redirectUrl.value}`
            window.location.href = `sms:?&body=${encodeURIComponent(text)}`
        }
    }

    const shareOnReddit = () => {
        if (redirectUrl.value) {
            const title = encodeURIComponent(
                `Check out my fantasy creation with ${
                    selectedModel.value?.name || 'this creator'
                }!`,
            )
            const url = encodeURIComponent(redirectUrl.value)
            window.open(`https://reddit.com/submit?url=${url}&title=${title}`, '_blank')
        }
    }

    const shareOnWhatsApp = () => {
        if (redirectUrl.value) {
            const text = encodeURIComponent(`Check out my fantasy creation: ${redirectUrl.value}`)
            window.open(`https://wa.me/?text=${text}`, '_blank')
        }
    }

    const shareOnTelegram = () => {
        if (redirectUrl.value) {
            const text = encodeURIComponent(`Check out my fantasy creation`)
            const url = encodeURIComponent(redirectUrl.value)
            window.open(`https://t.me/share/url?url=${url}&text=${text}`, '_blank')
        }
    }

    const currentStep = ref(1)
    const characteristics = ref(null)
    const models = ref([])
    const types = ref(null)
    const steps = ref(null)
    const selectedCharacteristics = ref({})

    // Log steps info after steps is initialized
    watch(steps, () => {
        if (steps.value) {
            console.log('primaryVoice Create modal - steps.length:', steps.value.length)
            console.log(
                'primaryVoice Create modal - total steps should be:',
                props.primaryVoice ? steps.value.length + 1 : steps.value.length + 2,
            )
        }
    })
    const customCharacteristics = ref({})
    const isOtherInputVisible = ref({})
    const ageCategory = ref({}) // Store age category (Teen, Adult, MILF, Elderly)
    const isStepValid = ref(false)
    const description = ref('')
    const scenarioName = ref('')
    const name = ref('')
    const gender = ref('female') // 'male' or 'female'
    const selectedModelId = ref(null)
    const exitMessageVisible = ref(false)
    const isLoading = ref(false)
    const errors = ref(null)
    const customErrors = ref({})
    const ageErrors = ref({})
    const bannedWords = ref([])
    const checkWordLoading = ref(false)
    const validWords = ref([])
    const notValidWords = ref([])
    const selectedModel = ref(null)
    const redirectUrl = ref(null)
    const createdFantasy = ref(null)
    const showSendToModelModal = ref(false)
    const showShareModal = ref(false)
    const isSubmittingSendToCreator = ref(false)
    const copied = ref(false)
    const isSentSuccess = ref(false)
    const wordCheckUnknownError = ref(false)
    const errorToast = ref(null)
    const showTooltip = ref(false)
    const isTokenModalOpen = ref(false)

    // Keep internal selected model in sync with the `model` prop when the modal opens.
    // Without this, changing the parent-selected character requires closing/reopening
    // the modal before `modelDisplayName`/voice labels update.
    watch(
        () => props.isOpen,
        isOpen => {
            if (!isOpen) return
            // Only set selected model if there's a primary voice configured
            selectedModel.value = (props.primaryVoice && props.model) || null
        },
        { immediate: true },
    )

    // If the parent swaps the model while the modal is already open, reflect it immediately.
    watch(
        () => props.model,
        model => {
            if (!props.isOpen) return
            // Only set selected model if there's a primary voice configured
            selectedModel.value = (props.primaryVoice && model) || null
        },
        { deep: false },
    )

    // Ref for scrollable containers
    const scrollableContainer = ref(null)
    const voiceScrollableContainer = ref(null)

    const randomId = () => {
        return parseInt(new Date().getTime() + '' + Math.floor(Math.random() * 100)).toString(16)
    }

    // Handler for when scrollable component mounts and emits its container
    const handleScrollableMounted = container => {
        scrollableContainer.value = container
    }

    const handleVoiceScrollableMounted = container => {
        voiceScrollableContainer.value = container
    }

    // Toggle tooltip visibility
    const toggleTooltip = () => {
        showTooltip.value = !showTooltip.value
    }

    // Watch for step changes and reset scroll position
    watch(currentStep, () => {
        // Hide tooltip when step changes
        showTooltip.value = false
        // Reset scroll position for the main steps scrollable
        if (scrollableContainer.value) {
            scrollableContainer.value.scrollTop = 0
        }
        // Reset scroll position for the voice selection scrollable
        if (voiceScrollableContainer.value) {
            voiceScrollableContainer.value.scrollTop = 0
        }
    })

    // Watch for step validity changes and hide tooltip when step becomes valid
    watch(isStepValid, newValue => {
        if (newValue) {
            showTooltip.value = false
        }
    })

    const saveCurrentSession = () => {
        if (!sessionId.value) {
            return
        }
        localStorage.setItem(
            'fantasy-session' + sessionId.value,
            JSON.stringify({
                currentStep: currentStep.value,
                characteristics: selectedCharacteristics.value,
                customCharacteristics: customCharacteristics.value,
                description: description.value,
                scenarioName: scenarioName.value,
                name: name.value,
                selectedModelId: selectedModelId.value,
                isOtherInputVisible: isOtherInputVisible.value,
                ageCategory: ageCategory.value,
            }),
        )
    }

    const setCurrentSession = session => {
        const sessionData = localStorage.getItem('fantasy-session' + session)
        if (sessionData) {
            const data = JSON.parse(sessionData)
            currentStep.value = data.currentStep || 0
            selectedCharacteristics.value = data.characteristics || {}
            customCharacteristics.value = data.customCharacteristics || {}
            description.value = data.description || ''
            scenarioName.value = data.scenarioName || ''
            name.value = data.name || ''
            selectedModelId.value = data.selectedModelId || null
            isOtherInputVisible.value = data.isOtherInputVisible || {}
            ageCategory.value = data.ageCategory || {}
        }
    }

    const saveSessionId = () => {
        const session = randomId()
        sessionId.value = session
        axios
            .post('/fantasy-characters-sessions', {
                session_id: session,
                url: page.url,
            })
            .then()
            .catch(error => {
                console.error('Error saving session:', error)
            })
    }

    const getCharacteristics = () => {
        return axios
            .get('/fantasy-characters-modal')
            .then(response => {
                let stepNumber = 1
                // Add defensive checks for steps
                steps.value = (response.data.steps || []).map(step => ({
                    step: stepNumber++,
                    ...step,
                }))
                // Add defensive checks for types
                types.value = (response.data.types || [])
                    .map(item => ({
                        ...item,
                        characteristics: (item.characteristics || [])
                            .map(x => x?.id)
                            .filter(Boolean),
                    }))
                    .reduce((obj, item) => {
                        if (item && item.id) {
                            obj[item.id] = item
                        }
                        return obj
                    }, {})
                // Add defensive checks for characteristics
                characteristics.value = (response.data.characteristics || []).reduce(
                    (obj, item) => {
                        if (item && item.id) {
                            obj[item.id] = item
                        }
                        return obj
                    },
                    {},
                )
            })
            .catch(error => {
                console.error('Error fetching characteristics:', error)
            })
    }

    const getModels = () => {
        return axios
            .get('/fantasy-characters-models')
            .then(response => {
                models.value = response.data
                setValidStep()
                if (selectedModelId.value && !selectedModel.value) {
                    selectedModel.value = models.value.find(
                        model => model.id === selectedModelId.value,
                    )
                }
            })
            .catch(error => {
                console.error('Error fetching models:', error)
            })
    }

    const saveCharacter = () => {
        isLoading.value = true

        const payload = {
            session_id: sessionId.value,
            gabber_lander_id: selectedModelId.value,
            characteristics: Object.values(selectedCharacteristics.value)
                .flat()
                .filter(id => id !== 'other'), // Filter out "other" values - they're sent as custom_characteristics
            description: description.value,
            name: name.value,
            scenario: scenarioName.value,
            custom_characteristics: Object.keys(isOtherInputVisible.value)
                .filter(x => !!isOtherInputVisible.value[x])
                .map(key => ({
                    type_id: key,
                    name: customCharacteristics.value[key] || '',
                    category_id:
                        types.value[key] &&
                        types.value[key].characteristics &&
                        types.value[key].characteristics.length > 0 &&
                        characteristics.value[types.value[key].characteristics[0]] &&
                        characteristics.value[types.value[key].characteristics[0]].category_id
                            ? characteristics.value[types.value[key].characteristics[0]].category_id
                            : 1,
                })),
        }

        // Check specifically for Occupation and Location
        const occupationTypeId = findTypeIdByName('occupation')
        const locationTypeId = findTypeIdByName('location')

        axios
            .post('/fantasy-characters', payload)
            .then(response => {
                isLoading.value = false
                exitMessageVisible.value = true
                createdFantasy.value = response.data
                redirectUrl.value = response.data.url
            })
            .catch(error => {
                isLoading.value = false
                bannedWords.value = []
                if (error.response && error.response.data) {
                    if (error.response.data.errors) {
                        errors.value = error.response.data.errors
                        Object.keys(error.response.data.errors).forEach(key => {
                            if (
                                key.includes('custom_characteristic') &&
                                error.response.data.errors[key].length
                            ) {
                                error.response.data.errors[key].forEach(err => {
                                    if (err.includes('banned words')) {
                                        const words = err.split(': ')
                                        bannedWords.value.push(words[1] || words[0])
                                    }
                                })
                            }
                        })
                    } else if (
                        error.response.data.message &&
                        error.response.data.message.includes(
                            'Insufficient fantasy creations or balance',
                        )
                    ) {
                        isTokenModalOpen.value = true
                    }
                } else {
                    errorToast.value =
                        'Oops something went wrong, please try again later. You were not charged for this fantasy'
                    setTimeout(() => {
                        errorToast.value = null
                    }, 5000)
                }
            })
    }

    const checkWord = (word, type) => {
        customErrors.value[type] = {}
        if (validWords.value.includes(word)) {
            setValidStep()
            return
        } else if (notValidWords.value.includes(word)) {
            customErrors.value[type] = {
                characteristic: [
                    'This characteristic includes the following banned words: ' + word,
                ],
            }
            setValidStep()
            return
        }
        checkWordLoading.value = true
        wordCheckUnknownError.value = false
        axios
            .post('/fantasy-characteristic-check', { characteristic: word })
            .then(response => {
                if (response.data.isValid) {
                    validWords.value.push(word)
                } else {
                    notValidWords.value.push(word)
                }
            })
            .catch(error => {
                console.error('Error checking word:', error)
                if (error.response && error.response.data && error.response.data.errors) {
                    notValidWords.value.push(word)
                    customErrors.value[type] = error.response.data.errors
                } else {
                    wordCheckUnknownError.value = { word, type }
                    customErrors.value[type] = {
                        characteristic: ['Error checking word. Please try again:'],
                    }
                }
            })
            .finally(() => {
                checkWordLoading.value = false
                setValidStep()
            })
    }

    const checkForBannedWords = async () => {
        // currentStep matches step numbers directly (no offset needed now)
        const currentStepItem = steps.value.find(item => item.step === currentStep.value)
        if (!currentStepItem) return

        return currentStepItem.characteristic_types.forEach(type => {
            if (isOtherInputVisible.value[type] && customCharacteristics.value[type]) {
                checkWord(customCharacteristics.value[type], type)

                // Check age validation
                const typeData = types.value[type]
                if (typeData?.name?.toLowerCase() === 'age') {
                    const ageNum = parseInt(customCharacteristics.value[type])
                    if (!isNaN(ageNum) && ageNum < 18) {
                        ageErrors.value[type] = ['Age must be 18 or older']
                    } else {
                        delete ageErrors.value[type]
                    }
                }
            }
        })
    }

    // Helper function to generate random age based on category
    const generateRandomAge = categoryName => {
        const category = categoryName.toLowerCase()

        if (category.includes('teen')) {
            // Teen (18+): 18-19
            return Math.floor(Math.random() * 2) + 18
        } else if (category === 'adult') {
            // Adult: 25-40
            return Math.floor(Math.random() * 16) + 25
        } else if (category === 'milf') {
            // MILF: 35-55
            return Math.floor(Math.random() * 21) + 35
        } else if (category === 'elderly') {
            // Elderly: 60-80
            return Math.floor(Math.random() * 21) + 60
        }

        return null
    }

    const handleClick = (type, id) => {
        isOtherInputVisible.value[type.id] = false
        if (!type.is_multiple) {
            selectedCharacteristics.value[type.id] = [id]
        } else {
            if (selectedCharacteristics.value[type.id]?.includes(id)) {
                const index = selectedCharacteristics.value[type.id].indexOf(id)
                if (index > -1) {
                    selectedCharacteristics.value[type.id].splice(index, 1)
                }
            } else {
                if (!selectedCharacteristics.value[type.id]) {
                    selectedCharacteristics.value[type.id] = []
                }
                selectedCharacteristics.value[type.id].push(id)
            }
        }

        // Auto-generate numeric age for age categories
        if (type.name?.toLowerCase() === 'age') {
            const characteristic = characteristics.value[id]
            if (characteristic && characteristic.name !== 'Other') {
                const randomAge = generateRandomAge(characteristic.name)
                if (randomAge) {
                    // Store the category name (Teen, Adult, MILF, Elderly)
                    ageCategory.value[type.id] = characteristic.name
                    // Set as custom age without showing the input field
                    customCharacteristics.value[type.id] = randomAge.toString()
                }
            } else if (characteristic && characteristic.name === 'Other') {
                // Clear age category when Other is selected
                delete ageCategory.value[type.id]
            }
        }

        setValidStep()
    }

    const clearError = field => {
        if (errors.value && errors.value[field]) {
            delete errors.value[field]
        }
    }

    const otherOpen = type => {
        isOtherInputVisible.value[type.id] = !isOtherInputVisible.value[type.id]
        if (!type.is_multiple) {
            selectedCharacteristics.value[type.id] = []
        }
        setValidStep()
    }

    const handleNextButton = () => {
        // When voice is pre-selected, scenario is the last step (no voice selection step)
        const isLastStep = props.primaryVoice
            ? currentStep.value === steps.value.length + 1 // Scenario step is last when voice pre-selected
            : currentStep.value === steps.value.length + 2 // Voice selection step is last otherwise

        if (!isLastStep) {
            currentStep.value++
            setValidStep()

            // Scroll to step indicator on mobile after DOM updates
            nextTick(() => {
                const stepIndicator = document.getElementById('step-indicator')
                if (stepIndicator && window.innerWidth < 768) {
                    stepIndicator.scrollIntoView({ behavior: 'smooth', block: 'start' })
                }
            })
        } else {
            if (!userHasSubscription.value) {
                subscriptionModalVisible.value = true
                return
            }
            // check for balance before submitting
            if (
                userBalance.value < fantasyCreationPrice.value &&
                (!remainingFantasyCreations.value || remainingFantasyCreations.value <= 0)
            ) {
                isTokenModalOpen.value = true
            } else {
                saveCharacter()
            }
        }
    }
    const handleBackButton = () => {
        if (currentStep.value > 1) {
            currentStep.value--
            setValidStep()

            // Scroll to top of modal on mobile after DOM updates
            nextTick(() => {
                const modalContent = document.getElementById('modal-content')
                if (modalContent && window.innerWidth < 768) {
                    modalContent.scrollIntoView({ behavior: 'smooth', block: 'start' })
                }
            })
        } else {
            // Handle cancel action
            handleClose()
        }
    }

    const handleModelClick = modelId => {
        selectedModelId.value = modelId
        selectedModel.value = models.value.find(model => model.id === modelId)
        setValidStep()
    }

    const scenarioStepFilled = () => {
        if (currentStep.value !== steps.value.length + 1) return false
        if (!description.value || !scenarioName.value) {
            return false
        }
        // Enforce minimum length requirements
        if (description.value.length < 30 || scenarioName.value.length < 3) {
            return false
        }
        return true
    }

    const voiceStepFilled = () => {
        return currentStep.value === steps.value.length + 2 && !!selectedModelId.value
    }

    const isStepFilled = () => {
        if (checkWordLoading.value) {
            return false
        }

        // currentStep matches step numbers directly (no offset needed now)
        const currentStepItem = steps.value.find(item => item.step === currentStep.value)
        if (!currentStepItem) {
            return false
        }

        // Check if character name is filled (only for first characteristic step)
        if (currentStepItem.step === 1) {
            if (!name.value || name.value.length < 3) {
                return false
            }
        }

        const result = currentStepItem.characteristic_types.every(type => {
            const typeData = types.value[type]

            // If "Other" input is visible, validate the custom input
            if (isOtherInputVisible.value[type]) {
                const customValue = customCharacteristics.value[type]

                let isValid =
                    customValue &&
                    customValue.length >= 2 &&
                    !notValidWords.value.includes(customValue)

                // Special validation for Age - must be 18+
                if (isValid && typeData?.name?.toLowerCase() === 'age') {
                    const ageNum = parseInt(customValue)
                    if (!isNaN(ageNum) && ageNum < 18) {
                        isValid = false
                    }
                }
                return isValid
            }

            // Check if a characteristic is selected
            const selected = selectedCharacteristics.value[type]

            if (!selected || selected.length === 0) {
                return false
            }

            // If "other" is selected in dropdown but input is empty
            if (selected[0] === 'other') {
                return false
            }
            return true
        })

        return result
    }

    function setValidStep() {
        if (!steps.value) return

        isStepValid.value = voiceStepFilled() || scenarioStepFilled() || isStepFilled()
        if (isStepValid.value) {
            saveCurrentSession()
        }
    }

    // Generate scenario based on character picks
    const generateScenarioFromCharacter = () => {
        if (!types.value || !characteristics.value) return

        // Pronoun helpers based on gender
        const pronouns = {
            subject: gender.value === 'male' ? 'he' : 'she',
            object: gender.value === 'male' ? 'him' : 'her',
            possessive: gender.value === 'male' ? 'his' : 'her',
            possessivePronoun: gender.value === 'male' ? 'his' : 'hers',
            reflexive: gender.value === 'male' ? 'himself' : 'herself',
        }

        // Gather character details
        const characterDetails = {}

        Object.keys(selectedCharacteristics.value).forEach(typeId => {
            const type = types.value[typeId]
            if (!type) return

            const typeName = type.name.toLowerCase()

            // Check if custom value exists
            if (customCharacteristics.value[typeId]) {
                characterDetails[typeName] = customCharacteristics.value[typeId]
            } else {
                // Get selected characteristic(s)
                const selectedIds = selectedCharacteristics.value[typeId]
                if (selectedIds && selectedIds.length > 0) {
                    // Handle multiple selections (like hobbies)
                    if (selectedIds.length > 1) {
                        const names = selectedIds
                            .map(id => characteristics.value[id]?.name)
                            .filter(name => name)
                        if (names.length > 0) {
                            characterDetails[typeName] = names
                        }
                    } else {
                        // Single selection
                        const char = characteristics.value[selectedIds[0]]
                        if (char) {
                            characterDetails[typeName] = char.name
                        }
                    }
                }
            }
        })

        // Generate title (keep original capitalization for titles)
        const characterName = name.value || 'Character'
        const age = characterDetails.age || ''
        const occupation = characterDetails.occupation || ''
        const relationship = characterDetails.relationship || ''

        let title = ''
        if (relationship && occupation) {
            title = `${characterName}: Your ${relationship} the ${occupation}`
        } else if (relationship) {
            title = `${characterName}: Your ${relationship}`
        } else if (occupation) {
            title = `${characterName} the ${occupation}`
        } else {
            title = `A Fantasy with ${characterName}`
        }

        // Generate description (lowercase all characteristics for descriptions)
        const personality = (characterDetails.personality || 'charming').toLowerCase()
        const ethnicity = (characterDetails.ethnicity || '').toLowerCase()
        const location = (characterDetails.location || 'your place').toLowerCase()
        const bodytype = (characterDetails['body type'] || '').toLowerCase()
        const occupationLower = (characterDetails.occupation || '').toLowerCase()
        const relationshipLower = (characterDetails.relationship || '').toLowerCase()
        const hobbies = characterDetails.hobbies || characterDetails.hobby || null

        // Get age category if it was selected (Teen, Adult, MILF, Elderly)
        const ageTypeId = Object.keys(types.value).find(
            id => types.value[id]?.name?.toLowerCase() === 'age',
        )
        const selectedAgeCategory = ageTypeId ? ageCategory.value[ageTypeId] : null

        // Helper function to get age descriptor based on age range
        const getAgeDescriptor = age => {
            const ageNum = parseInt(age)
            if (!isNaN(ageNum)) {
                if (ageNum >= 18 && ageNum <= 19) {
                    const descriptors = ['youthful', 'vibrant', 'energetic', 'fresh', 'lively']
                    return descriptors[Math.floor(Math.random() * descriptors.length)]
                } else if (ageNum >= 20 && ageNum <= 34) {
                    const descriptors = [
                        'stunning',
                        'captivating',
                        'alluring',
                        'radiant',
                        'magnetic',
                    ]
                    return descriptors[Math.floor(Math.random() * descriptors.length)]
                } else if (ageNum >= 35 && ageNum <= 55) {
                    const descriptors = [
                        'gorgeous',
                        'sophisticated',
                        'sultry',
                        'mesmerizing',
                        'irresistible',
                    ]
                    return descriptors[Math.floor(Math.random() * descriptors.length)]
                } else if (ageNum >= 56) {
                    const descriptors = [
                        'distinguished',
                        'elegant',
                        'refined',
                        'graceful',
                        'enchanting',
                    ]
                    return descriptors[Math.floor(Math.random() * descriptors.length)]
                }
            }
            return 'captivating'
        }

        // Helper function to format age descriptions naturally
        const formatAgeDescription = (age, variant = 'default') => {
            if (!age) return ''

            // Handle numeric ages
            const ageNum = parseInt(age)
            if (!isNaN(ageNum)) {
                const descriptor = getAgeDescriptor(age)
                if (variant === 'years') return `${ageNum} years of captivating presence`
                if (variant === 'energy') return `${ageNum} years of magnetic energy`
                if (variant === 'this') return `this ${ageNum}-year-old ${descriptor}`
                if (variant === 'descriptor') return `a ${ageNum}-year-old ${descriptor}`
                return `a ${ageNum}-year-old` // default without descriptor
            }

            // Handle age categories
            const ageLower = age.toLowerCase()

            if (ageLower.includes('teen')) {
                if (variant === 'years') return 'youthful energy'
                if (variant === 'energy') return 'young and vibrant'
                if (variant === 'this') return 'this vibrant young adult'
                if (variant === 'descriptor') return 'a vibrant young adult (18+)'
                return 'a young adult (18+)'
            }

            if (ageLower === 'adult') {
                if (variant === 'years') return 'mature confidence'
                if (variant === 'energy') return 'experienced and captivating'
                if (variant === 'this') return 'this stunning adult'
                if (variant === 'descriptor') return 'a stunning experienced adult'
                return 'an experienced adult'
            }

            if (ageLower === 'milf') {
                if (variant === 'years') return 'sophisticated allure'
                if (variant === 'energy') return 'mature and irresistible'
                if (variant === 'this') return 'this captivating MILF'
                if (variant === 'descriptor') return 'a gorgeous sophisticated MILF'
                return 'a gorgeous MILF'
            }

            if (ageLower === 'elderly') {
                if (variant === 'years') return 'distinguished presence'
                if (variant === 'energy') return 'wise and alluring'
                if (variant === 'this') return 'this elegant distinguished individual'
                if (variant === 'descriptor') return 'a distinguished elegant older person'
                return 'a distinguished older person'
            }

            // Fallback for any other custom text
            if (variant === 'years') return 'captivating presence'
            if (variant === 'energy') return 'magnetic energy'
            if (variant === 'this') return `this captivating ${age}`
            if (variant === 'descriptor') return `a captivating ${age}`
            return `a ${age}`
        }

        let descParts = []

        // Helper function to randomly select from array
        const pickRandom = arr => arr[Math.floor(Math.random() * arr.length)]

        // Helper to build intro description parts
        const buildIntroDetails = (includeDescriptor = true) => {
            let parts = []

            if (age && includeDescriptor) {
                parts.push(formatAgeDescription(age, 'descriptor'))
            } else if (age) {
                parts.push(formatAgeDescription(age))
            }

            // Add age category if selected (MILF, Teen, etc.)
            if (selectedAgeCategory && selectedAgeCategory.toLowerCase() !== 'other') {
                const categoryLower = selectedAgeCategory.toLowerCase()
                // Only add if it's MILF or a recognizable category
                if (
                    categoryLower === 'milf' ||
                    categoryLower.includes('teen') ||
                    categoryLower === 'elderly'
                ) {
                    parts.push(selectedAgeCategory)
                }
            }

            if (ethnicity) {
                parts.push(ethnicity)
            }

            if (occupationLower) {
                parts.push(occupationLower)
            }

            return parts.length > 0 ? `, ${parts.join(' ')}` : ''
        }

        // Introduction variations
        const introVariations = [
            `You meet ${characterName}${buildIntroDetails()}.`,
            `${characterName} enters your world${
                age ? `, ${formatAgeDescription(age, 'years')}` : ''
            }${ethnicity ? `, ${ethnicity}` : ''}${
                occupationLower ? `, working as ${occupationLower}` : ''
            }.`,
            `Your paths cross with ${characterName}${buildIntroDetails()}.`,
            `${characterName} appears${age ? ` - ${formatAgeDescription(age, 'energy')}` : ''}${
                ethnicity ? `, with ${ethnicity} features that mesmerize you` : ''
            }${
                occupationLower ? `, their life as ${occupationLower} adding to their mystique` : ''
            }.`,
            `There's something unforgettable about ${characterName}${
                age ? `, ${formatAgeDescription(age, 'this')}` : ''
            }${ethnicity ? ` ${ethnicity}` : ''}${
                occupationLower ? ` who works as ${occupationLower}` : ''
            } who's now standing before you.`,
        ]
        descParts.push(pickRandom(introVariations))

        // Personality variations
        if (personality) {
            const personalityVariations = [
                `${
                    pronouns.subject.charAt(0).toUpperCase() + pronouns.subject.slice(1)
                } has a ${personality} personality that draws you in immediately.`,
                `${
                    pronouns.possessive.charAt(0).toUpperCase() + pronouns.possessive.slice(1)
                } ${personality} nature makes every moment feel electric.`,
                `What strikes you first is ${pronouns.possessive} ${personality} energy - it's absolutely irresistible.`,
                `The way ${pronouns.subject} carries ${pronouns.reflexive} with that ${personality} demeanor leaves you breathless.`,
                `You're captivated by ${pronouns.possessive} ${personality} charm from the very first glance.`,
            ]
            descParts.push(pickRandom(personalityVariations))
        }

        // Hobbies variations
        if (hobbies) {
            const formatHobbies = () => {
                if (Array.isArray(hobbies)) {
                    const lowercasedHobbies = hobbies.map(h => h.toLowerCase())
                    if (lowercasedHobbies.length === 1) {
                        return lowercasedHobbies[0]
                    } else if (lowercasedHobbies.length === 2) {
                        return `${lowercasedHobbies[0]} and ${lowercasedHobbies[1]}`
                    } else {
                        const last = lowercasedHobbies[lowercasedHobbies.length - 1]
                        const rest = lowercasedHobbies.slice(0, -1).join(', ')
                        return `${rest}, and ${last}`
                    }
                }
                return hobbies.toLowerCase()
            }

            const hobbiesText = formatHobbies()
            const hobbiesVariations = [
                `When ${pronouns.subject}'s not working, ${pronouns.subject} loves ${hobbiesText} - passions that make ${pronouns.object} even more intriguing.`,
                `${
                    pronouns.possessive.charAt(0).toUpperCase() + pronouns.possessive.slice(1)
                } interests in ${hobbiesText} reveal a depth that draws you in further.`,
                `You discover ${pronouns.subject} enjoys ${hobbiesText}, which adds another layer to ${pronouns.possessive} appeal.`,
                `${
                    pronouns.subject.charAt(0).toUpperCase() + pronouns.subject.slice(1)
                } shares that ${hobbiesText} are among ${
                    pronouns.possessive
                } favorite pastimes, sparking instant connection.`,
                `The way ${pronouns.subject} lights up talking about ${hobbiesText} shows a side of ${pronouns.object} you can't resist.`,
            ]
            descParts.push(pickRandom(hobbiesVariations))
        }

        // Body type variations
        if (bodytype) {
            const bodytypeVariations = [
                `${
                    pronouns.possessive.charAt(0).toUpperCase() + pronouns.possessive.slice(1)
                } ${bodytype} figure is captivating.`,
                `You find yourself mesmerized by ${pronouns.possessive} ${bodytype} physique.`,
                `${
                    pronouns.possessive.charAt(0).toUpperCase() + pronouns.possessive.slice(1)
                } ${bodytype} body moves with a grace that's impossible to ignore.`,
                `Every curve of ${pronouns.possessive} ${bodytype} form tells a story you want to read.`,
                `That ${bodytype} silhouette creates a magnetic pull you can't resist.`,
            ]
            descParts.push(pickRandom(bodytypeVariations))
        }

        // Location variations
        if (location) {
            const locationVariations = [
                `The encounter takes place at ${location}, setting the perfect mood for what's to come.`,
                `${location} becomes the backdrop for this unforgettable moment between you.`,
                `Something about ${location} makes everything feel more intense, more real.`,
                `The atmosphere at ${location} crackles with anticipation as you draw closer.`,
                `At ${location}, the world fades away until it's just the two of you.`,
            ]
            descParts.push(pickRandom(locationVariations))
        }

        // Relationship variations
        if (relationshipLower) {
            const relationshipVariations = [
                `As your ${relationshipLower}, there's already a special connection between you.`,
                `The fact that ${pronouns.subject}'s your ${relationshipLower} adds a thrilling layer to every interaction.`,
                `Being your ${relationshipLower}, ${pronouns.subject} knows exactly how to make your heart race.`,
                `Your ${relationshipLower} has always had this effect on you, but tonight it's different - more intense.`,
                `The bond you share as ${relationshipLower} creates an intimacy that words can't capture.`,
            ]
            descParts.push(pickRandom(relationshipVariations))
        }

        // Chemistry/conclusion variations
        const conclusionVariations = [
            `The chemistry between you is undeniable, and the tension builds with every moment.`,
            `Every glance, every word exchanged seems to ignite something deeper between you both.`,
            `The air between you feels charged, thick with unspoken desire and anticipation.`,
            `Time seems to slow as the connection intensifies, pulling you both closer together.`,
            `There's an unspoken understanding passing between you - this is just the beginning.`,
        ]
        descParts.push(pickRandom(conclusionVariations))

        scenarioName.value = title
        description.value = descParts.join(' ')

        // Validate after generation
        setValidStep()
    }

    // Random pick function for current step
    const randomPickCurrentStep = () => {
        if (!steps.value || !types.value || !characteristics.value) return

        // Check if we're on the scenario step
        if (currentStep.value === steps.value.length + 1) {
            generateScenarioFromCharacter()
            return
        }

        const currentStepItem = steps.value.find(item => item.step === currentStep.value)
        if (!currentStepItem) return

        // Randomly select characteristics for each type in current step
        currentStepItem.characteristic_types.forEach(typeId => {
            const type = types.value[typeId]
            if (!type || !type.characteristics) return

            // Get available characteristics (excluding "Other")
            const availableChars = type.characteristics
                .map(id => characteristics.value[id])
                .filter(c => c && c.name !== 'Other')

            if (availableChars.length > 0) {
                // Pick a random characteristic
                const randomChar = availableChars[Math.floor(Math.random() * availableChars.length)]

                // Update selection
                selectedCharacteristics.value[typeId] = [randomChar.id]

                // Auto-generate numeric age for age categories
                if (type.name?.toLowerCase() === 'age' && randomChar.name !== 'Other') {
                    const randomAge = generateRandomAge(randomChar.name)
                    if (randomAge) {
                        // Store the category name (Teen, Adult, MILF, Elderly)
                        ageCategory.value[typeId] = randomChar.name
                        // Set as custom age without showing the input field
                        customCharacteristics.value[typeId] = randomAge.toString()
                    }
                    // Clear "Other" input visibility regardless
                    isOtherInputVisible.value[typeId] = false
                } else {
                    // Clear "Other" input if it was visible
                    isOtherInputVisible.value[typeId] = false
                    customCharacteristics.value[typeId] = ''
                }
            }
        })

        // If it's step 1, always generate a random name
        if (currentStepItem.step === 1) {
            const randomNames = [
                'Alex',
                'Jordan',
                'Casey',
                'Morgan',
                'Riley',
                'Taylor',
                'Cameron',
                'Quinn',
                'Avery',
                'Parker',
                'Skyler',
                'Dakota',
                'Jamie',
                'Sage',
                'Phoenix',
                'River',
                'Blake',
                'Reese',
                'Dylan',
                'Charlie',
                'Aspen',
                'Hayden',
                'Kendall',
                'Peyton',
            ]
            name.value = randomNames[Math.floor(Math.random() * randomNames.length)]

            // Scroll to step indicator on mobile when on step 1
            nextTick(() => {
                const stepIndicator = document.getElementById('step-indicator')
                if (stepIndicator && window.innerWidth < 768) {
                    stepIndicator.scrollIntoView({ behavior: 'smooth', block: 'start' })
                }
            })
        }

        // Validate the step after random selection
        setValidStep()
    }

    // Get list of missing/invalid fields for current step
    const getMissingFields = computed(() => {
        if (!steps.value) return []
        const missing = []

        // Check if word validation is in progress
        if (checkWordLoading.value) {
            missing.push('Checking for inappropriate words...')
            return missing
        }

        // Scenario step validation
        if (currentStep.value === steps.value.length + 1) {
            if (!scenarioName.value || scenarioName.value.length < 3) {
                missing.push('Scenario name (minimum 3 characters)')
            }
            if (!description.value || description.value.length < 30) {
                missing.push('Description (minimum 30 characters)')
            }
            // Add helpful hint if both fields are empty
            if (
                (!scenarioName.value || scenarioName.value.length === 0) &&
                (!description.value || description.value.length === 0)
            ) {
                missing.push(
                    '💡 Tip: Use "Generate Story" button to auto-create from your character!',
                )
            }
            return missing
        }

        // Voice step validation
        if (currentStep.value === steps.value.length + 2) {
            if (!selectedModelId.value) {
                missing.push('Select a voice model')
            }
            return missing
        }

        // Characteristic steps validation
        const currentStepItem = steps.value.find(item => item.step === currentStep.value)
        if (!currentStepItem) return missing

        // Check character name (step 1 only)
        if (currentStepItem.step === 1) {
            if (!name.value || name.value.length < 3) {
                missing.push('Character name (minimum 3 characters)')
            }
        }

        // Check each characteristic type in current step
        currentStepItem.characteristic_types.forEach(type => {
            const typeData = types.value[type]
            const typeName = typeData?.name || 'Field'

            // Check custom "Other" input
            if (isOtherInputVisible.value[type]) {
                const customValue = customCharacteristics.value[type]

                if (!customValue || customValue.length < 2) {
                    missing.push(`${typeName} (minimum 2 characters)`)
                } else if (notValidWords.value.includes(customValue)) {
                    missing.push(`${typeName} (contains inappropriate word)`)
                } else if (typeData?.name?.toLowerCase() === 'age') {
                    const ageNum = parseInt(customValue)
                    if (!isNaN(ageNum) && ageNum < 18) {
                        missing.push(`${typeName} (must be 18 or older)`)
                    }
                }
            } else {
                // Check regular selection
                const selected = selectedCharacteristics.value[type]
                if (!selected || selected.length === 0) {
                    missing.push(`Select ${typeName}`)
                } else if (selected[0] === 'other') {
                    missing.push(`${typeName} (complete "Other" field)`)
                }
            }
        })

        // Add helpful hint if multiple fields are missing (2+ fields)
        if (missing.length >= 2) {
            missing.push('💡 Tip: Use "Random Pick" button to quickly fill all fields!')
        }

        return missing
    })

    // Helper methods for preview panel
    const getSelectedAge = () => {
        const ageTypeId = findTypeIdByName('age')
        if (!ageTypeId) return null

        // Check if custom age value is set (auto-generated or manual "Other")
        if (customCharacteristics.value[ageTypeId]) {
            return customCharacteristics.value[ageTypeId]
        }

        const selectedIds = selectedCharacteristics.value[ageTypeId]
        if (!selectedIds || !selectedIds.length) return null

        const charId = selectedIds[0]
        return characteristics.value[charId]?.name || null
    }

    const getSelectedPersonality = () => {
        const personalityTypeId = findTypeIdByName('personality')
        if (!personalityTypeId) return null

        if (
            isOtherInputVisible.value[personalityTypeId] &&
            customCharacteristics.value[personalityTypeId]
        ) {
            return customCharacteristics.value[personalityTypeId]
        }

        const selectedIds = selectedCharacteristics.value[personalityTypeId]
        if (!selectedIds || !selectedIds.length) return null

        const charId = selectedIds[0]
        return characteristics.value[charId]?.name || null
    }

    const getSelectedRelationship = () => {
        const relationshipTypeId = findTypeIdByName('relationship')
        if (!relationshipTypeId) return null

        if (
            isOtherInputVisible.value[relationshipTypeId] &&
            customCharacteristics.value[relationshipTypeId]
        ) {
            return customCharacteristics.value[relationshipTypeId]
        }

        const selectedIds = selectedCharacteristics.value[relationshipTypeId]
        if (!selectedIds || !selectedIds.length) return null

        const charId = selectedIds[0]
        return characteristics.value[charId]?.name || null
    }

    const getSelectedOccupation = () => {
        const occupationTypeId = findTypeIdByName('occupation')
        if (!occupationTypeId) return null

        if (
            isOtherInputVisible.value[occupationTypeId] &&
            customCharacteristics.value[occupationTypeId]
        ) {
            return customCharacteristics.value[occupationTypeId]
        }

        const selectedIds = selectedCharacteristics.value[occupationTypeId]
        if (!selectedIds || !selectedIds.length) return null

        const charId = selectedIds[0]
        return characteristics.value[charId]?.name || null
    }

    const getSelectedLocation = () => {
        const locationTypeId = findTypeIdByName('location')
        if (!locationTypeId) return null

        if (
            isOtherInputVisible.value[locationTypeId] &&
            customCharacteristics.value[locationTypeId]
        ) {
            return customCharacteristics.value[locationTypeId]
        }

        const selectedIds = selectedCharacteristics.value[locationTypeId]
        if (!selectedIds || !selectedIds.length) return null

        const charId = selectedIds[0]
        return characteristics.value[charId]?.name || null
    }

    const getSelectedHobbies = () => {
        const hobbiesTypeId = findTypeIdByName('hobbies')
        if (!hobbiesTypeId) return []

        // Check if custom "Other" value is set
        if (
            isOtherInputVisible.value[hobbiesTypeId] &&
            customCharacteristics.value[hobbiesTypeId]
        ) {
            return [customCharacteristics.value[hobbiesTypeId]]
        }

        const selectedIds = selectedCharacteristics.value[hobbiesTypeId]
        if (!selectedIds || !selectedIds.length) return []

        return selectedIds.map(id => characteristics.value[id]?.name).filter(Boolean)
    }

    const findTypeIdByName = searchName => {
        if (!types.value) return null

        const searchLower = searchName.toLowerCase()
        for (const [typeId, type] of Object.entries(types.value)) {
            if (type.name.toLowerCase().includes(searchLower)) {
                return parseInt(typeId)
            }
        }
        return null
    }

    const getStepForCharacteristicType = typeName => {
        const typeId = findTypeIdByName(typeName)
        if (!typeId || !steps.value) return 1

        for (const step of steps.value) {
            if (step.characteristic_types && step.characteristic_types.includes(typeId)) {
                return step.step // No offset needed - steps match directly
            }
        }
        return 1
    }

    const jumpToStep = step => {
        if (step >= 1 && step <= steps.value.length + 2) {
            currentStep.value = step
            setValidStep()
        }
    }

    const hasCharacteristicImages = type => {
        if (!type || !type.characteristics || !characteristics.value) {
            return false
        }

        // Check if any characteristic in this type has an avatar
        return type.characteristics.some(charId => {
            const char = characteristics.value[charId]
            return char && char.avatar
        })
    }

    const isRelationshipType = type => {
        if (!type || !type.name) return false
        // Check if the type name contains "relationship"
        return type.name.toLowerCase().includes('relationship')
    }

    const isSelectDropdownType = type => {
        if (!type || !type.name) return false
        const typeName = type.name.toLowerCase()
        // Check if the type is occupation or location
        return typeName.includes('occupation') || typeName.includes('location')
    }

    const getSortedCharacteristics = type => {
        if (!type || !type.characteristics || !characteristics.value) {
            return []
        }

        // Map characteristic IDs to characteristic objects
        const chars = type.characteristics.map(id => characteristics.value[id]).filter(c => !!c)

        // For relationship, occupation, and location types, sort alphabetically with "Other" at the end
        if (isRelationshipType(type) || isSelectDropdownType(type)) {
            return chars.sort((a, b) => {
                if (a.name === 'Other') return 1
                if (b.name === 'Other') return -1
                return a.name.localeCompare(b.name)
            })
        }

        // For other types, return as-is
        return chars
    }

    function tryCheckWordAgain(word, type) {
        if (!checkWordLoading.value) {
            checkWord(word, type)
        }
    }

    watch([currentStep, description, name, scenarioName, steps], () => {
        setValidStep()
    })

    // saves session one time.
    watch([currentStep, sessionId], () => {
        if (props.isOpen && currentStep.value === 1 && !sessionId.value) {
            saveSessionId()
        }
    })

    // Prevent body scrolling when modal is open
    watch(
        () => props.isOpen,
        newVal => {
            if (newVal) {
                document.body.style.overflow = 'hidden'
            } else {
                document.body.style.overflow = 'auto'
            }
        },
    )

    onMounted(() => {
        if (!user.value) return
        getCharacteristics()
        getModels() // Always load models for voice selection
        // check session
        if (sessionId.value) {
            setCurrentSession(sessionId.value)
        }
        // Only pre-select model if there's a primary voice configured
        if (props.primaryVoice && props.model && landerId.value) {
            selectedModelId.value = landerId.value
            selectedModel.value = props.model
            setValidStep()
        }
    })

    // Cleanup on component unmount
    onUnmounted(() => {
        document.body.style.overflow = 'auto'
    })
</script>

<template>
    <!-- Fantasy Creator Modal -->
    <Teleport to="body">
        <div
            class="fixed inset-0 z-[1000] flex items-center justify-center"
            v-if="props.isOpen && steps && steps.length">
            <!-- Backdrop -->
            <div
                class="absolute inset-0 bg-black bg-opacity-70 backdrop-blur-sm"
                @click="handleClose"></div>

            <!-- Modal content -->
            <div
                class="relative max-h-[100dvh] w-full max-w-5xl touch-manipulation overflow-y-auto rounded-2xl bg-[#121212] pt-8 shadow-2xl md:max-h-[90vh]"
                id="create-fantasy"
                :class="{ 'px-2 md:px-6': !exitMessageVisible }">
                <!-- Close button -->
                <button
                    @click="handleClose"
                    class="absolute right-4 top-4 z-10 h-[36px] w-[36px] rounded-full bg-[#222222] p-2 text-white hover:bg-[#333333]">
                    <font-awesome-icon :icon="faClose" class="h-5 w-5" />
                </button>

                <!-- Only show header on first step and NOT in success view -->
                <div class="p-2" v-if="!exitMessageVisible && currentStep === 1">
                    <h1 class="mb-2 text-center text-3xl font-bold text-white">
                        Create Your Fantasy
                    </h1>
                    <p class="mb-2 text-center text-lg text-gray-300">
                        Design your perfect scenario by customizing every detail of your experience.
                    </p>
                </div>

                <!-- Streamlined Creator Layout -->
                <div
                    id="modal-content"
                    v-if="!exitMessageVisible"
                    class="overflow-hidden rounded-xl">
                    <!-- Step Indicator -->
                    <div id="step-indicator" class="border-b border-[#2a2a2a] bg-[#121212] p-4">
                        <div class="mb-2 flex items-center justify-between">
                            <div class="text-sm text-gray-400">
                                Step {{ currentStep }} of
                                {{ props.primaryVoice ? steps.length + 1 : steps.length + 2 }}
                            </div>
                            <span class="font-medium text-[#65e8a4]">
                                {{
                                    Math.round(
                                        ((currentStep - 1) /
                                            (props.primaryVoice
                                                ? steps.length + 1
                                                : steps.length + 2)) *
                                            100,
                                    )
                                }}% Complete
                            </span>
                        </div>
                        <div class="mb-4 flex h-2 overflow-hidden rounded bg-[#222222] text-xs">
                            <div
                                :style="{
                                    width: `${Math.round(
                                        ((currentStep - 1) /
                                            (props.primaryVoice
                                                ? steps.length + 1
                                                : steps.length + 2)) *
                                            100,
                                    )}%`,
                                }"
                                class="flex flex-col justify-center whitespace-nowrap bg-gradient-to-r from-[#65e8a4] to-[#41966a] text-center text-white shadow-none"></div>
                        </div>
                    </div>

                    <!-- Two Panel Layout -->
                    <div class="flex flex-col md:flex-row">
                        <!-- Left side - Form (3/5) -->
                        <div
                            class="relative min-h-[100vh] bg-[#1a1a1a] p-4 pb-32 md:min-h-0 md:w-3/5 md:p-6 md:pb-24">
                            <div
                                class="space-y-6"
                                v-show="currentStep >= 1 && currentStep <= steps.length">
                                <scrollable
                                    class="max-h-[100%] md:max-h-[440px]"
                                    @mounted="handleScrollableMounted">
                                    <div
                                        class="p-2 pr-3 md:pr-2"
                                        v-for="step in steps"
                                        :key="step.step"
                                        v-show="currentStep === step.step">
                                        <!-- Step Header -->
                                        <h2 class="mb-4 text-2xl font-bold text-white">
                                            {{ step.name || 'Character Details' }}
                                        </h2>
                                        <p class="mb-6 text-gray-300">
                                            {{
                                                step.description ||
                                                "Define the character's attributes"
                                            }}
                                        </p>

                                        <div class="space-y-6">
                                            <!-- Character Name (only show on first characteristic step) -->
                                            <div v-if="step.step === 1">
                                                <div class="mb-2 flex items-center justify-between">
                                                    <label class="font-medium text-[#65e8a4]">
                                                        Character Name
                                                    </label>
                                                    <!-- Gender Selection -->
                                                    <div class="flex gap-2">
                                                        <button
                                                            type="button"
                                                            @click="gender = 'female'"
                                                            class="flex h-8 w-8 items-center justify-center rounded-lg transition-all"
                                                            :class="
                                                                gender === 'female'
                                                                    ? 'bg-pink-500 text-white'
                                                                    : 'bg-[#222222] text-gray-400 hover:bg-[#2a2a2a]'
                                                            "
                                                            title="Female">
                                                            <svg
                                                                xmlns="http://www.w3.org/2000/svg"
                                                                class="h-5 w-5"
                                                                viewBox="0 0 24 24"
                                                                fill="none"
                                                                stroke="currentColor"
                                                                stroke-width="2"
                                                                stroke-linecap="round"
                                                                stroke-linejoin="round">
                                                                <circle cx="12" cy="7" r="4" />
                                                                <path d="M12 11v8" />
                                                                <path d="M9 16h6" />
                                                            </svg>
                                                        </button>
                                                        <button
                                                            type="button"
                                                            @click="gender = 'male'"
                                                            class="flex h-8 w-8 items-center justify-center rounded-lg transition-all"
                                                            :class="
                                                                gender === 'male'
                                                                    ? 'bg-blue-500 text-white'
                                                                    : 'bg-[#222222] text-gray-400 hover:bg-[#2a2a2a]'
                                                            "
                                                            title="Male">
                                                            <svg
                                                                xmlns="http://www.w3.org/2000/svg"
                                                                class="h-5 w-5"
                                                                viewBox="0 0 24 24"
                                                                fill="none"
                                                                stroke="currentColor"
                                                                stroke-width="2"
                                                                stroke-linecap="round"
                                                                stroke-linejoin="round">
                                                                <circle cx="10" cy="14" r="4" />
                                                                <path d="M14.5 9.5l5-5" />
                                                                <path d="M15 5h4.5V9.5" />
                                                            </svg>
                                                        </button>
                                                    </div>
                                                </div>
                                                <input
                                                    v-model="name"
                                                    type="text"
                                                    class="w-full rounded-lg border bg-[#222222] px-4 py-3 text-white focus:outline-none focus:ring-2"
                                                    :class="
                                                        errors?.name
                                                            ? 'border-red-500 focus:ring-red-500'
                                                            : 'border-[#333333] focus:ring-[#65e8a4]'
                                                    "
                                                    placeholder="Enter character name..." />
                                                <laravel-error-message
                                                    v-if="errors?.name"
                                                    :errors="errors.name" />
                                                <p
                                                    v-if="name.length > 0 && name.length < 3"
                                                    class="mt-1 text-sm text-yellow-400">
                                                    Name should be at least 3 characters
                                                </p>
                                            </div>

                                            <div
                                                v-for="type in step.characteristic_types
                                                    .map(type => types[type])
                                                    .filter(t => !!t)"
                                                :key="type.id">
                                                <label
                                                    name="selectedCharacteristics[type.id]-label"
                                                    class="mb-2 block font-medium text-[#65e8a4]">
                                                    {{ type.name }}
                                                </label>
                                                <p
                                                    v-if="type.name.toLowerCase() === 'hobbies'"
                                                    class="mb-3 text-xs text-gray-400">
                                                    You can select multiple hobbies
                                                </p>

                                                <!-- Select dropdown for Occupation and Location -->
                                                <template v-if="isSelectDropdownType(type)">
                                                    <select
                                                        :id="`selectedCharacteristics-${type.id}-dropdown`"
                                                        v-model="selectedCharacteristics[type.id]"
                                                        @change="
                                                            () => {
                                                                if (
                                                                    selectedCharacteristics[
                                                                        type.id
                                                                    ] &&
                                                                    selectedCharacteristics[type.id]
                                                                        .length > 0
                                                                ) {
                                                                    const selectedValue =
                                                                        selectedCharacteristics[
                                                                            type.id
                                                                        ][0]
                                                                    if (selectedValue === 'other') {
                                                                        isOtherInputVisible[
                                                                            type.id
                                                                        ] = true
                                                                    } else {
                                                                        isOtherInputVisible[
                                                                            type.id
                                                                        ] = false
                                                                        customCharacteristics[
                                                                            type.id
                                                                        ] = ''
                                                                    }
                                                                }
                                                                setValidStep()
                                                            }
                                                        "
                                                        class="mb-2 w-full rounded-lg border border-[#333333] bg-[#222222] px-4 py-3 text-white focus:outline-none focus:ring-2 focus:ring-[#65e8a4]">
                                                        <option value="">
                                                            Select {{ type.name.toLowerCase() }}
                                                        </option>
                                                        <option
                                                            v-for="char in getSortedCharacteristics(
                                                                type,
                                                            )"
                                                            :key="char.id"
                                                            :value="[char.id]">
                                                            {{ char.name }}
                                                        </option>
                                                        <option :value="['other']">Other</option>
                                                    </select>
                                                    <!-- Custom input for "Other" in select mode -->
                                                    <div
                                                        v-if="isOtherInputVisible[type.id]"
                                                        class="mt-3">
                                                        <label
                                                            class="mb-2 block font-medium text-[#65e8a4]">
                                                            Custom {{ type.name }}
                                                        </label>
                                                        <input
                                                            v-model="customCharacteristics[type.id]"
                                                            @input="
                                                                () => {
                                                                    checkForBannedWords()
                                                                    setValidStep()
                                                                }
                                                            "
                                                            type="text"
                                                            class="mb-2 w-full rounded-lg border bg-[#222222] px-4 py-3 text-white focus:outline-none focus:ring-2"
                                                            :class="{
                                                                'border-red-500 focus:ring-red-500':
                                                                    (bannedWords?.length &&
                                                                        bannedWords.includes(
                                                                            customCharacteristics[
                                                                                type.id
                                                                            ],
                                                                        )) ||
                                                                    notValidWords?.includes(
                                                                        customCharacteristics[
                                                                            type.id
                                                                        ],
                                                                    ) ||
                                                                    ageErrors?.[type.id],
                                                                'border-green-500 focus:ring-green-500':
                                                                    validWords?.includes(
                                                                        customCharacteristics[
                                                                            type.id
                                                                        ],
                                                                    ) && !ageErrors?.[type.id],
                                                                'border-[#333333] focus:ring-[#65e8a4]':
                                                                    !bannedWords?.includes(
                                                                        customCharacteristics[
                                                                            type.id
                                                                        ],
                                                                    ) &&
                                                                    !notValidWords?.includes(
                                                                        customCharacteristics[
                                                                            type.id
                                                                        ],
                                                                    ) &&
                                                                    !validWords?.includes(
                                                                        customCharacteristics[
                                                                            type.id
                                                                        ],
                                                                    ) &&
                                                                    !ageErrors?.[type.id],
                                                            }"
                                                            :placeholder="`Enter ${type.name.toLowerCase()}...`" />
                                                        <laravel-error-message
                                                            v-if="ageErrors?.[type.id]"
                                                            :errors="ageErrors[type.id]" />
                                                        <laravel-error-message
                                                            v-if="
                                                                customErrors?.[type.id]
                                                                    ?.characteristic
                                                            "
                                                            :errors="
                                                                customErrors[type.id].characteristic
                                                            " />
                                                    </div>
                                                </template>

                                                <!-- Check if any characteristic has an avatar to determine display mode -->
                                                <template v-else-if="hasCharacteristicImages(type)">
                                                    <!-- Image-based selection with grid layout (like React component) -->
                                                    <div
                                                        class="grid grid-cols-2 gap-3 md:grid-cols-3">
                                                        <button
                                                            v-for="char in (
                                                                type.characteristics || []
                                                            )
                                                                .map(id => characteristics[id])
                                                                .filter(c => !!c)"
                                                            :key="char.id"
                                                            type="button"
                                                            class="relative overflow-hidden rounded-lg transition-all"
                                                            :class="
                                                                selectedCharacteristics[
                                                                    type.id
                                                                ]?.includes(char.id)
                                                                    ? 'ring-2 ring-[#65e8a4]'
                                                                    : 'hover:opacity-80'
                                                            "
                                                            @click="
                                                                () => handleClick(type, char.id)
                                                            ">
                                                            <img
                                                                v-if="char.avatar"
                                                                :src="char.avatar"
                                                                :alt="char.name"
                                                                class="h-24 w-full object-cover opacity-40" />
                                                            <div
                                                                v-else
                                                                class="h-24 w-full bg-[#222222]"></div>
                                                            <div
                                                                class="absolute inset-0 flex items-center justify-center"
                                                                :class="
                                                                    selectedCharacteristics[
                                                                        type.id
                                                                    ]?.includes(char.id)
                                                                        ? 'bg-[#65e8a4] bg-opacity-40'
                                                                        : ''
                                                                ">
                                                                <span
                                                                    class="text-lg font-semibold text-white">
                                                                    {{ char.name }}
                                                                </span>
                                                            </div>
                                                            <div
                                                                v-if="
                                                                    selectedCharacteristics[
                                                                        type.id
                                                                    ]?.includes(char.id)
                                                                "
                                                                class="absolute right-2 top-2 flex h-6 w-6 items-center justify-center rounded-full bg-[#65e8a4]">
                                                                <font-awesome-icon
                                                                    :icon="faCheck"
                                                                    class="h-4 w-4 text-black" />
                                                            </div>
                                                        </button>

                                                        <!-- Other option for image mode -->
                                                        <button
                                                            type="button"
                                                            class="relative overflow-hidden rounded-lg transition-all"
                                                            :class="
                                                                isOtherInputVisible[type.id]
                                                                    ? 'ring-2 ring-[#65e8a4]'
                                                                    : 'hover:opacity-80'
                                                            "
                                                            @click="() => otherOpen(type)">
                                                            <div
                                                                class="h-24 w-full bg-[#222222]"></div>
                                                            <div
                                                                class="absolute inset-0 flex items-center justify-center"
                                                                :class="
                                                                    isOtherInputVisible[type.id]
                                                                        ? 'bg-[#65e8a4] bg-opacity-40'
                                                                        : ''
                                                                ">
                                                                <span
                                                                    class="text-lg font-semibold text-white">
                                                                    Other
                                                                </span>
                                                            </div>
                                                            <div
                                                                v-if="isOtherInputVisible[type.id]"
                                                                class="absolute right-2 top-2 flex h-6 w-6 items-center justify-center rounded-full bg-[#65e8a4]">
                                                                <font-awesome-icon
                                                                    :icon="faCheck"
                                                                    class="h-4 w-4 text-black" />
                                                            </div>
                                                        </button>
                                                    </div>
                                                </template>

                                                <template v-else>
                                                    <!-- Button-based selection (for text-only options like age, personality, etc.) -->
                                                    <!-- Use grid layout for relationship types, flex for others -->
                                                    <div
                                                        :class="
                                                            isRelationshipType(type)
                                                                ? 'mb-4 grid grid-cols-2 gap-4'
                                                                : 'flex flex-wrap gap-3'
                                                        ">
                                                        <button
                                                            v-for="char in getSortedCharacteristics(
                                                                type,
                                                            )"
                                                            :key="char.id"
                                                            type="button"
                                                            :class="[
                                                                isRelationshipType(type)
                                                                    ? 'p-4 text-center'
                                                                    : 'px-4 py-2',
                                                                'rounded-lg transition-colors',
                                                                selectedCharacteristics[
                                                                    type.id
                                                                ]?.includes(char.id)
                                                                    ? 'bg-[#65e8a4] text-black'
                                                                    : 'bg-[#222222] text-white hover:bg-[#404040]',
                                                            ]"
                                                            @click="
                                                                () => handleClick(type, char.id)
                                                            ">
                                                            {{ char.name }}
                                                        </button>

                                                        <!-- Other button -->
                                                        <button
                                                            type="button"
                                                            :class="[
                                                                isRelationshipType(type)
                                                                    ? 'p-4 text-center'
                                                                    : 'px-4 py-2',
                                                                'rounded-lg transition-colors',
                                                                isOtherInputVisible[type.id]
                                                                    ? 'bg-[#444444] text-white'
                                                                    : 'bg-[#222222] text-white hover:bg-[#404040]',
                                                            ]"
                                                            @click="() => otherOpen(type)">
                                                            Other
                                                        </button>
                                                    </div>
                                                </template>

                                                <!-- Custom input for "Other" (for button-based selections only) -->
                                                <div
                                                    v-if="
                                                        isOtherInputVisible[type.id] &&
                                                        !isSelectDropdownType(type)
                                                    "
                                                    class="mt-3">
                                                    <label
                                                        v-if="!hasCharacteristicImages(type)"
                                                        class="mb-2 block font-medium text-[#65e8a4]">
                                                        Custom {{ type.name }}
                                                    </label>
                                                    <AutoFocusInput
                                                        :id="`custom-characteristic-${type.id}`"
                                                        v-model="customCharacteristics[type.id]"
                                                        @input="
                                                            () => {
                                                                checkForBannedWords()
                                                                setValidStep()
                                                            }
                                                        "
                                                        type="text"
                                                        class="mb-2 w-full rounded-lg border bg-[#222222] px-4 py-3 text-white focus:outline-none focus:ring-2"
                                                        :class="{
                                                            'border-red-500 focus:ring-red-500':
                                                                (bannedWords?.length &&
                                                                    bannedWords.includes(
                                                                        customCharacteristics[
                                                                            type.id
                                                                        ],
                                                                    )) ||
                                                                notValidWords?.includes(
                                                                    customCharacteristics[type.id],
                                                                ) ||
                                                                ageErrors?.[type.id],
                                                            'border-green-500 focus:ring-green-500':
                                                                validWords?.includes(
                                                                    customCharacteristics[type.id],
                                                                ) && !ageErrors?.[type.id],
                                                            'border-[#333333] focus:ring-[#65e8a4]':
                                                                !bannedWords?.includes(
                                                                    customCharacteristics[type.id],
                                                                ) &&
                                                                !notValidWords?.includes(
                                                                    customCharacteristics[type.id],
                                                                ) &&
                                                                !validWords?.includes(
                                                                    customCharacteristics[type.id],
                                                                ) &&
                                                                !ageErrors?.[type.id],
                                                        }"
                                                        :placeholder="`Enter ${type.name.toLowerCase()}...`" />
                                                    <laravel-error-message
                                                        v-if="ageErrors?.[type.id]"
                                                        :errors="ageErrors[type.id]" />
                                                    <laravel-error-message
                                                        v-if="
                                                            customErrors?.[type.id]?.characteristic
                                                        "
                                                        :errors="
                                                            customErrors[type.id].characteristic
                                                        " />
                                                    <button
                                                        v-if="
                                                            wordCheckUnknownError &&
                                                            wordCheckUnknownError.type === type.id
                                                        "
                                                        class="inline-flex rounded-md bg-indigo-600 px-3 py-1 text-white hover:bg-indigo-700"
                                                        @click="
                                                            tryCheckWordAgain(
                                                                wordCheckUnknownError.word,
                                                                wordCheckUnknownError.type,
                                                            )
                                                        ">
                                                        Try again
                                                    </button>
                                                    <div
                                                        v-if="isLoading || checkWordLoading"
                                                        class="absolute right-4 top-1/2 -translate-y-1/2">
                                                        <svg-spin-loader :is-loading="true" />
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </scrollable>
                            </div>
                            <div v-show="currentStep === steps.length + 1">
                                <h2 class="mb-4 text-2xl font-bold text-white">Scenario Details</h2>
                                <p class="mb-6 text-gray-300">Describe your fantasy scenario</p>

                                <div class="space-y-6">
                                    <!-- Scenario Title -->
                                    <div>
                                        <label class="mb-2 block font-medium text-[#65e8a4]">
                                            Fantasy Title
                                        </label>
                                        <input
                                            v-model="scenarioName"
                                            type="text"
                                            class="w-full rounded-lg border bg-[#222222] px-4 py-3 text-white focus:outline-none focus:ring-2"
                                            :class="
                                                errors?.scenario
                                                    ? 'border-red-500 focus:ring-red-500'
                                                    : 'border-[#333333] focus:ring-[#65e8a4]'
                                            "
                                            placeholder="Enter Fantasy Title..." />
                                        <laravel-error-message
                                            v-if="errors?.scenario"
                                            :errors="errors.scenario" />
                                        <p
                                            v-if="
                                                scenarioName.length > 0 && scenarioName.length < 3
                                            "
                                            class="mt-1 text-sm text-yellow-400">
                                            Title should be at least 3 characters
                                        </p>
                                    </div>

                                    <!-- Description -->
                                    <div>
                                        <label class="mb-2 block font-medium text-[#65e8a4]">
                                            Description
                                        </label>
                                        <textarea
                                            v-model="description"
                                            class="min-h-[40vh] w-full rounded-lg border bg-[#222222] px-4 py-3 text-white focus:outline-none focus:ring-2 md:min-h-[150px]"
                                            :class="
                                                errors?.description
                                                    ? 'border-red-500 focus:ring-red-500'
                                                    : 'border-[#333333] focus:ring-[#65e8a4]'
                                            "
                                            @input="() => clearError('description')"
                                            placeholder="Tell the AI what is going on between it and the user.
Use You to describe the AI, and Me to describe the end user"></textarea>
                                        <p
                                            class="mt-1 text-sm"
                                            :class="
                                                description.length < 30 && description.length > 0
                                                    ? 'text-yellow-400'
                                                    : 'text-gray-400'
                                            ">
                                            {{ description.length }} characters
                                            {{
                                                description.length < 30
                                                    ? ' (minimum 30 required)'
                                                    : ''
                                            }}
                                        </p>

                                        <laravel-error-message
                                            v-if="errors?.description"
                                            :errors="errors.description" />
                                    </div>

                                    <!-- Banned Words Warning -->
                                    <div
                                        v-if="bannedWords?.length"
                                        class="rounded-lg border border-red-500 bg-red-500/10 p-3">
                                        <p class="mb-2 text-sm text-red-400">
                                            The following words are not allowed:
                                        </p>
                                        <div class="flex flex-wrap gap-2">
                                            <span
                                                v-for="(word, index) in bannedWords"
                                                :key="index"
                                                class="rounded bg-red-500/20 px-2 py-1 text-xs text-red-300">
                                                {{ word }}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <!-- Voice Selection Step (Final Step - only shown when voice not pre-selected) -->
                            <div v-show="!props.primaryVoice && currentStep === steps.length + 2">
                                <h2 class="mb-4 text-2xl font-bold text-white">Select a Voice</h2>
                                <p class="mb-6 text-gray-300">
                                    Choose whose voice you want to interact with
                                </p>

                                <scrollable
                                    class="max-h-[60vh]"
                                    @mounted="handleVoiceScrollableMounted">
                                    <div class="grid grid-cols-1 gap-4 pr-3 md:grid-cols-2 md:pr-2">
                                        <div
                                            v-for="model in models"
                                            :key="model.id"
                                            class="cursor-pointer rounded-lg border bg-[#1a1a1a] p-4 transition-all"
                                            :class="
                                                selectedModelId === model.id
                                                    ? 'border-[#65e8a4] ring-2 ring-[#65e8a4]'
                                                    : 'border-[#2a2a2a] hover:bg-[#222222]'
                                            "
                                            @click="() => handleModelClick(model.id)">
                                            <div class="flex items-center">
                                                <img
                                                    v-if="model.avatar"
                                                    :src="model.avatar"
                                                    :alt="model.name"
                                                    class="mr-4 h-14 w-14 rounded-full object-cover" />
                                                <div
                                                    v-else
                                                    class="mr-4 flex h-14 w-14 items-center justify-center rounded-full bg-[#222222]">
                                                    <font-awesome-icon
                                                        :icon="faUser"
                                                        class="h-8 w-8 text-gray-500" />
                                                </div>
                                                <div class="flex-1">
                                                    <h3 class="font-medium text-white">
                                                        {{ model.name }}
                                                    </h3>
                                                    <p class="text-sm text-gray-400">
                                                        {{
                                                            model.voice_id ===
                                                            '626c3b02-2d2a-4a93-b3e7-be35fd2b95cd'
                                                                ? 'Fantasy Voice'
                                                                : 'Celebrity Voice'
                                                        }}
                                                    </p>
                                                </div>
                                                <font-awesome-icon
                                                    v-if="selectedModelId === model.id"
                                                    :icon="faCheck"
                                                    class="h-5 w-5 text-[#65e8a4]" />
                                            </div>
                                        </div>
                                    </div>
                                </scrollable>
                            </div>
                            <!-- Validation message -->
                            <div
                                v-if="!isStepValid && currentStep > 1"
                                class="mt-4 flex items-center justify-center text-sm text-[#65e8a4]">
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    class="mr-2 h-4 w-4"
                                    fill="none"
                                    viewBox="0 0 24 24"
                                    stroke="currentColor">
                                    <path
                                        stroke-linecap="round"
                                        stroke-linejoin="round"
                                        stroke-width="2"
                                        d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                                <span>Please complete all required fields to continue</span>
                            </div>
                            <div
                                v-else
                                class="mt-4 flex h-[20px] w-full items-center justify-center text-sm text-[#65e8a4]"></div>
                            <!-- Navigation Buttons -->
                            <div
                                class="fixed bottom-0 left-0 right-0 z-50 border-t border-[#2a2a2a] bg-[#1a1a1a] p-4 md:absolute md:left-0 md:right-0 md:border-t md:p-4">
                                <div class="flex items-center justify-between">
                                    <button
                                        @click="handleBackButton"
                                        class="w-[110px] rounded-lg bg-[#222222] px-4 py-2 text-white transition-colors hover:bg-[#2a2a2a]">
                                        {{ currentStep > 1 ? 'Back' : 'Cancel' }}
                                    </button>

                                    <!-- Random Pick Button (for characteristic and scenario steps) -->
                                    <button
                                        v-if="currentStep <= steps.length + 1"
                                        @click="randomPickCurrentStep"
                                        class="flex h-[40px] touch-manipulation select-none items-center gap-2 rounded-lg bg-purple-600 px-3 py-2 text-[12px] text-white transition-colors hover:bg-purple-700 md:text-sm"
                                        :title="
                                            currentStep === steps.length + 1
                                                ? 'Generate scenario from character'
                                                : 'Randomly select options for this step'
                                        ">
                                        <svg
                                            v-if="currentStep === steps.length + 1"
                                            class="h-4 w-4"
                                            fill="none"
                                            stroke="currentColor"
                                            viewBox="0 0 24 24">
                                            <path
                                                stroke-linecap="round"
                                                stroke-linejoin="round"
                                                stroke-width="2"
                                                d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                                        </svg>
                                        <svg
                                            v-else
                                            class="hidden h-4 w-4 md:block"
                                            fill="none"
                                            stroke="currentColor"
                                            viewBox="0 0 24 24">
                                            <path
                                                stroke-linecap="round"
                                                stroke-linejoin="round"
                                                stroke-width="2"
                                                d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                                        </svg>
                                        {{
                                            currentStep === steps.length + 1
                                                ? 'Generate Story'
                                                : 'Random Pick'
                                        }}
                                    </button>

                                    <div
                                        v-if="
                                            (props.primaryVoice &&
                                                currentStep < steps.length + 1) ||
                                            (!props.primaryVoice && currentStep < steps.length + 2)
                                        "
                                        class="group relative">
                                        <!-- Mobile backdrop to close tooltip when clicking outside -->
                                        <div
                                            v-if="showTooltip"
                                            @click="showTooltip = false"
                                            class="fixed inset-0 z-40 md:hidden"></div>

                                        <button
                                            @click="
                                                isStepValid ? handleNextButton() : toggleTooltip()
                                            "
                                            class="relative z-50 flex w-[110px] items-center justify-center rounded-lg px-4 py-2 transition-colors"
                                            :class="
                                                isStepValid
                                                    ? 'bg-[#222222] text-white hover:bg-[#444444]'
                                                    : 'cursor-not-allowed bg-[#222222] text-gray-500'
                                            ">
                                            Continue
                                            <font-awesome-icon
                                                :icon="faArrowRight"
                                                class="ml-2 h-4 w-4" />
                                        </button>

                                        <!-- Tooltip for disabled button -->
                                        <div
                                            v-if="!isStepValid && getMissingFields.length > 0"
                                            @click.stop
                                            class="absolute bottom-full right-0 z-50 mb-2 w-64 rounded-lg bg-gray-900 px-4 py-3 text-sm text-white shadow-xl"
                                            :class="
                                                showTooltip
                                                    ? 'block'
                                                    : 'hidden md:group-hover:block'
                                            ">
                                            <div class="mb-1 font-semibold">
                                                Required to continue:
                                            </div>
                                            <ul class="list-inside space-y-1">
                                                <li
                                                    v-for="(field, index) in getMissingFields"
                                                    :key="index"
                                                    class="flex items-start">
                                                    <span class="mr-2 text-red-400">•</span>
                                                    <span>{{ field }}</span>
                                                </li>
                                            </ul>
                                            <!-- Arrow pointing down -->
                                            <div
                                                class="absolute right-4 top-full h-0 w-0 border-l-8 border-r-8 border-t-8 border-l-transparent border-r-transparent border-t-gray-900"></div>
                                        </div>
                                    </div>
                                    <!-- Subscribe button (shown when user doesn't have subscription and form is filled) -->
                                    <button
                                        v-else-if="
                                            !userHasSubscription && isTitleAndDescriptionFilled
                                        "
                                        @click="openSubscriptionModal"
                                        class="flex items-center rounded-lg bg-[#65e8a4] px-6 py-2 text-black transition-colors hover:bg-opacity-90">
                                        Create Fantasy
                                    </button>
                                    <!-- Create Fantasy button (only shown when user has subscription) -->
                                    <button
                                        v-else-if="userHasSubscription"
                                        @click="handleNextButton"
                                        :disabled="!isStepValid || isLoading"
                                        class="flex items-center rounded-lg bg-[#65e8a4] px-6 py-2 text-black transition-colors hover:bg-opacity-90 disabled:cursor-not-allowed disabled:bg-[#222222] disabled:text-gray-500">
                                        <svg-spin-loader
                                            v-if="isLoading || checkWordLoading"
                                            :is-loading="true" />
                                        <div
                                            class="flex items-center justify-center"
                                            v-if="remainingFantasyCreations <= 0">
                                            <span>Create for {{ fantasyCreationPrice }}</span>
                                            <img
                                                src="/images/fyf-token.png"
                                                alt="fyf token"
                                                class="ml-1 inline h-5 w-5" />
                                        </div>
                                        <span v-else> Create Free Fantasy </span>
                                    </button>
                                </div>
                            </div>
                        </div>

                        <!-- Right side - Preview (2/5) -->
                        <div class="hidden bg-[#121212] p-2 md:block md:w-2/5 md:p-6">
                            <h3 class="mb-4 text-xl font-semibold text-white">Preview</h3>
                            <div
                                class="max-h-[100%] overflow-y-auto rounded-xl bg-[#1a1a1a] p-5 md:max-h-[500px]">
                                <!-- Preview Content -->
                                <div class="flex h-full flex-col">
                                    <!-- Character Header -->
                                    <div class="mb-4 border-b border-[#2a2a2a] pb-4">
                                        <div class="flex items-center">
                                            <div
                                                v-if="selectedModel"
                                                class="mr-3 h-16 w-16 overflow-hidden rounded-full border-2 border-[#65e8a4]">
                                                <img
                                                    v-if="selectedModel.avatar"
                                                    :src="selectedModel.avatar"
                                                    :alt="modelDisplayName"
                                                    class="h-full w-full object-cover" />
                                                <div
                                                    v-else
                                                    class="flex h-full w-full items-center justify-center bg-[#222222]">
                                                    <font-awesome-icon
                                                        :icon="faUser"
                                                        class="h-8 w-8 text-gray-500" />
                                                </div>
                                            </div>
                                            <div
                                                v-else
                                                class="mr-3 flex h-16 w-16 items-center justify-center rounded-full bg-[#222222]">
                                                <font-awesome-icon
                                                    :icon="faUser"
                                                    class="h-8 w-8 text-gray-500" />
                                            </div>
                                            <div>
                                                <h3 class="text-lg font-medium text-white">
                                                    {{ name || 'Your Character' }}
                                                </h3>
                                                <div class="mt-1 flex items-center text-[#65e8a4]">
                                                    <span class="text-sm">
                                                        {{
                                                            selectedModel
                                                                ? `${selectedVoiceLabel}'s Voice`
                                                                : selectedVoiceLabel
                                                        }}
                                                    </span>
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    <!-- Attribute Cards -->
                                    <div class="flex-1">
                                        <div class="mb-4 grid grid-cols-1 gap-3">
                                            <!-- Age -->
                                            <div
                                                class="flex items-center justify-between rounded-lg bg-[#222222] p-3">
                                                <div class="flex items-center">
                                                    <font-awesome-icon
                                                        :icon="faUser"
                                                        class="mr-3 h-5 w-5 text-gray-400" />
                                                    <div>
                                                        <p class="text-xs text-gray-400">Age</p>
                                                        <p class="text-sm text-white">
                                                            <template v-if="getSelectedAge()">{{
                                                                getSelectedAge()
                                                            }}</template>
                                                            <span v-else class="text-gray-500"
                                                                >Not selected</span
                                                            >
                                                        </p>
                                                    </div>
                                                </div>
                                                <button
                                                    @click="
                                                        jumpToStep(
                                                            getStepForCharacteristicType('age'),
                                                        )
                                                    "
                                                    class="rounded-full p-1.5 text-gray-400 hover:bg-[#333333] hover:text-[#65e8a4]">
                                                    <template v-if="getSelectedAge()">
                                                        <svg
                                                            xmlns="http://www.w3.org/2000/svg"
                                                            class="h-4 w-4"
                                                            fill="none"
                                                            viewBox="0 0 24 24"
                                                            stroke="currentColor">
                                                            <path
                                                                stroke-linecap="round"
                                                                stroke-linejoin="round"
                                                                stroke-width="2"
                                                                d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                                                        </svg>
                                                    </template>
                                                    <span
                                                        v-else
                                                        class="rounded-full bg-[#222222] px-2 py-1 text-xs text-gray-300 hover:bg-[#444444]">
                                                        Add
                                                    </span>
                                                </button>
                                            </div>

                                            <!-- Personality -->
                                            <div
                                                class="flex items-center justify-between rounded-lg bg-[#222222] p-3">
                                                <div class="flex items-center">
                                                    <svg
                                                        xmlns="http://www.w3.org/2000/svg"
                                                        class="mr-3 h-5 w-5 text-gray-400"
                                                        fill="none"
                                                        viewBox="0 0 24 24"
                                                        stroke="currentColor">
                                                        <path
                                                            stroke-linecap="round"
                                                            stroke-linejoin="round"
                                                            stroke-width="2"
                                                            d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
                                                    </svg>
                                                    <div>
                                                        <p class="text-xs text-gray-400">
                                                            Personality
                                                        </p>
                                                        <p class="text-sm text-white">
                                                            <template
                                                                v-if="getSelectedPersonality()"
                                                                >{{
                                                                    getSelectedPersonality()
                                                                }}</template
                                                            >
                                                            <span v-else class="text-gray-500"
                                                                >Not selected</span
                                                            >
                                                        </p>
                                                    </div>
                                                </div>
                                                <button
                                                    @click="
                                                        jumpToStep(
                                                            getStepForCharacteristicType(
                                                                'personality',
                                                            ),
                                                        )
                                                    "
                                                    class="rounded-full p-1.5 text-gray-400 hover:bg-[#333333] hover:text-[#65e8a4]">
                                                    <template v-if="getSelectedPersonality()">
                                                        <svg
                                                            xmlns="http://www.w3.org/2000/svg"
                                                            class="h-4 w-4"
                                                            fill="none"
                                                            viewBox="0 0 24 24"
                                                            stroke="currentColor">
                                                            <path
                                                                stroke-linecap="round"
                                                                stroke-linejoin="round"
                                                                stroke-width="2"
                                                                d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                                                        </svg>
                                                    </template>
                                                    <span
                                                        v-else
                                                        class="rounded-full bg-[#222222] px-2 py-1 text-xs text-gray-300 hover:bg-[#444444]">
                                                        Add
                                                    </span>
                                                </button>
                                            </div>

                                            <!-- Relationship -->
                                            <div
                                                class="flex items-center justify-between rounded-lg bg-[#222222] p-3">
                                                <div class="flex items-center">
                                                    <svg
                                                        xmlns="http://www.w3.org/2000/svg"
                                                        class="mr-3 h-5 w-5 text-gray-400"
                                                        fill="none"
                                                        viewBox="0 0 24 24"
                                                        stroke="currentColor">
                                                        <path
                                                            stroke-linecap="round"
                                                            stroke-linejoin="round"
                                                            stroke-width="2"
                                                            d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                                                    </svg>
                                                    <div>
                                                        <p class="text-xs text-gray-400">
                                                            Relationship
                                                        </p>
                                                        <p class="text-sm text-white">
                                                            <template
                                                                v-if="getSelectedRelationship()"
                                                                >{{
                                                                    getSelectedRelationship()
                                                                }}</template
                                                            >
                                                            <span v-else class="text-gray-500"
                                                                >Not selected</span
                                                            >
                                                        </p>
                                                    </div>
                                                </div>
                                                <button
                                                    @click="
                                                        jumpToStep(
                                                            getStepForCharacteristicType(
                                                                'relationship',
                                                            ),
                                                        )
                                                    "
                                                    class="rounded-full p-1.5 text-gray-400 hover:bg-[#333333] hover:text-[#65e8a4]">
                                                    <template v-if="getSelectedRelationship()">
                                                        <svg
                                                            xmlns="http://www.w3.org/2000/svg"
                                                            class="h-4 w-4"
                                                            fill="none"
                                                            viewBox="0 0 24 24"
                                                            stroke="currentColor">
                                                            <path
                                                                stroke-linecap="round"
                                                                stroke-linejoin="round"
                                                                stroke-width="2"
                                                                d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                                                        </svg>
                                                    </template>
                                                    <span
                                                        v-else
                                                        class="rounded-full bg-[#222222] px-2 py-1 text-xs text-gray-300 hover:bg-[#444444]">
                                                        Add
                                                    </span>
                                                </button>
                                            </div>
                                            <!-- Hobbies -->
                                            <div
                                                class="flex items-center justify-between rounded-lg bg-[#222222] p-3">
                                                <div class="flex flex-1 items-center">
                                                    <svg
                                                        xmlns="http://www.w3.org/2000/svg"
                                                        class="mr-3 h-5 w-5 text-gray-400"
                                                        fill="none"
                                                        viewBox="0 0 24 24"
                                                        stroke="currentColor">
                                                        <path
                                                            stroke-linecap="round"
                                                            stroke-linejoin="round"
                                                            stroke-width="2"
                                                            d="M14.828 14.828a4 4 0 01-5.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                                    </svg>
                                                    <div class="flex-1">
                                                        <p class="text-xs text-gray-400">Hobbies</p>
                                                        <div class="text-sm text-white">
                                                            <template
                                                                v-if="getSelectedHobbies().length">
                                                                <div
                                                                    class="mt-1 flex flex-wrap gap-1">
                                                                    <span
                                                                        v-for="(
                                                                            hobby, index
                                                                        ) in getSelectedHobbies()"
                                                                        :key="index"
                                                                        class="rounded bg-[#65e8a4] bg-opacity-20 px-2 py-0.5 text-xs text-[#65e8a4]">
                                                                        {{ hobby }}
                                                                    </span>
                                                                </div>
                                                            </template>
                                                            <span v-else class="text-gray-500"
                                                                >Not selected</span
                                                            >
                                                        </div>
                                                    </div>
                                                </div>
                                                <button
                                                    @click="
                                                        jumpToStep(
                                                            getStepForCharacteristicType('hobbies'),
                                                        )
                                                    "
                                                    class="ml-2 rounded-full p-1.5 text-gray-400 hover:bg-[#333333] hover:text-[#65e8a4]">
                                                    <template v-if="getSelectedHobbies().length">
                                                        <svg
                                                            xmlns="http://www.w3.org/2000/svg"
                                                            class="h-4 w-4"
                                                            fill="none"
                                                            viewBox="0 0 24 24"
                                                            stroke="currentColor">
                                                            <path
                                                                stroke-linecap="round"
                                                                stroke-linejoin="round"
                                                                stroke-width="2"
                                                                d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                                                        </svg>
                                                    </template>
                                                    <span
                                                        v-else
                                                        class="rounded-full bg-[#222222] px-2 py-1 text-xs text-gray-300 hover:bg-[#444444]">
                                                        Add
                                                    </span>
                                                </button>
                                            </div>

                                            <!-- Occupation -->
                                            <div
                                                class="flex items-center justify-between rounded-lg bg-[#222222] p-3">
                                                <div class="flex items-center">
                                                    <svg
                                                        xmlns="http://www.w3.org/2000/svg"
                                                        class="mr-3 h-5 w-5 text-gray-400"
                                                        fill="none"
                                                        viewBox="0 0 24 24"
                                                        stroke="currentColor">
                                                        <path
                                                            stroke-linecap="round"
                                                            stroke-linejoin="round"
                                                            stroke-width="2"
                                                            d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                                                    </svg>
                                                    <div>
                                                        <p class="text-xs text-gray-400">
                                                            Occupation
                                                        </p>
                                                        <p class="text-sm text-white">
                                                            <template
                                                                v-if="getSelectedOccupation()"
                                                                >{{
                                                                    getSelectedOccupation()
                                                                }}</template
                                                            >
                                                            <span v-else class="text-gray-500"
                                                                >Not selected</span
                                                            >
                                                        </p>
                                                    </div>
                                                </div>
                                                <button
                                                    @click="
                                                        jumpToStep(
                                                            getStepForCharacteristicType(
                                                                'occupation',
                                                            ),
                                                        )
                                                    "
                                                    class="rounded-full p-1.5 text-gray-400 hover:bg-[#333333] hover:text-[#65e8a4]">
                                                    <template v-if="getSelectedOccupation()">
                                                        <svg
                                                            xmlns="http://www.w3.org/2000/svg"
                                                            class="h-4 w-4"
                                                            fill="none"
                                                            viewBox="0 0 24 24"
                                                            stroke="currentColor">
                                                            <path
                                                                stroke-linecap="round"
                                                                stroke-linejoin="round"
                                                                stroke-width="2"
                                                                d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                                                        </svg>
                                                    </template>
                                                    <span
                                                        v-else
                                                        class="rounded-full bg-[#222222] px-2 py-1 text-xs text-gray-300 hover:bg-[#444444]">
                                                        Add
                                                    </span>
                                                </button>
                                            </div>

                                            <!-- Location -->
                                            <div
                                                class="flex items-center justify-between rounded-lg bg-[#222222] p-3">
                                                <div class="flex items-center">
                                                    <svg
                                                        xmlns="http://www.w3.org/2000/svg"
                                                        class="mr-3 h-5 w-5 text-gray-400"
                                                        fill="none"
                                                        viewBox="0 0 24 24"
                                                        stroke="currentColor">
                                                        <path
                                                            stroke-linecap="round"
                                                            stroke-linejoin="round"
                                                            stroke-width="2"
                                                            d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                                                        <path
                                                            stroke-linecap="round"
                                                            stroke-linejoin="round"
                                                            stroke-width="2"
                                                            d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                                                    </svg>
                                                    <div>
                                                        <p class="text-xs text-gray-400">
                                                            Location
                                                        </p>
                                                        <p class="text-sm text-white">
                                                            <template
                                                                v-if="getSelectedLocation()"
                                                                >{{
                                                                    getSelectedLocation()
                                                                }}</template
                                                            >
                                                            <span v-else class="text-gray-500"
                                                                >Not selected</span
                                                            >
                                                        </p>
                                                    </div>
                                                </div>
                                                <button
                                                    @click="
                                                        jumpToStep(
                                                            getStepForCharacteristicType(
                                                                'location',
                                                            ),
                                                        )
                                                    "
                                                    class="rounded-full p-1.5 text-gray-400 hover:bg-[#333333] hover:text-[#65e8a4]">
                                                    <template v-if="getSelectedLocation()">
                                                        <svg
                                                            xmlns="http://www.w3.org/2000/svg"
                                                            class="h-4 w-4"
                                                            fill="none"
                                                            viewBox="0 0 24 24"
                                                            stroke="currentColor">
                                                            <path
                                                                stroke-linecap="round"
                                                                stroke-linejoin="round"
                                                                stroke-width="2"
                                                                d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                                                        </svg>
                                                    </template>
                                                    <span
                                                        v-else
                                                        class="rounded-full bg-[#222222] px-2 py-1 text-xs text-gray-300 hover:bg-[#444444]">
                                                        Add
                                                    </span>
                                                </button>
                                            </div>
                                        </div>

                                        <!-- Scenario section -->
                                        <div
                                            class="mt-4 flex justify-between rounded-lg bg-[#222222] p-4">
                                            <div class="flex-1">
                                                <h4 class="mb-2 font-medium text-white">
                                                    {{ scenarioName || 'Your Scenario' }}
                                                </h4>
                                                <p class="text-sm text-gray-300">
                                                    <template v-if="description">{{
                                                        description
                                                    }}</template>
                                                    <span v-else class="text-gray-500">
                                                        Add a description to see your scenario
                                                        details
                                                    </span>
                                                </p>
                                            </div>
                                            <button
                                                @click="jumpToStep(steps.length + 1)"
                                                class="h-fit rounded-full p-1.5 text-gray-400 hover:bg-[#333333] hover:text-[#65e8a4]">
                                                <template v-if="description">
                                                    <svg
                                                        xmlns="http://www.w3.org/2000/svg"
                                                        class="h-4 w-4"
                                                        fill="none"
                                                        viewBox="0 0 24 24"
                                                        stroke="currentColor">
                                                        <path
                                                            stroke-linecap="round"
                                                            stroke-linejoin="round"
                                                            stroke-width="2"
                                                            d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                                                    </svg>
                                                </template>
                                                <span
                                                    v-else
                                                    class="rounded-full bg-[#222222] px-2 py-1 text-xs text-gray-300 hover:bg-[#444444]">
                                                    Add
                                                </span>
                                            </button>
                                        </div>

                                        <!-- Required fields indicator -->
                                        <div class="mt-4 rounded-lg bg-[#222222] p-3">
                                            <p class="mb-2 text-sm text-gray-300">
                                                Required fields:
                                            </p>
                                            <div class="flex flex-wrap gap-2">
                                                <span
                                                    :class="`rounded-full px-2 py-1 text-xs ${
                                                        selectedModelId
                                                            ? 'bg-[#65e8a4] bg-opacity-20 text-[#65e8a4]'
                                                            : 'bg-red-500 bg-opacity-20 text-red-300'
                                                    }`">
                                                    Voice
                                                </span>
                                                <span
                                                    :class="`rounded-full px-2 py-1 text-xs ${
                                                        name && name.length >= 3
                                                            ? 'bg-[#65e8a4] bg-opacity-20 text-[#65e8a4]'
                                                            : 'bg-red-500 bg-opacity-20 text-red-300'
                                                    }`">
                                                    Character Name
                                                </span>
                                                <span
                                                    :class="`rounded-full px-2 py-1 text-xs ${
                                                        getSelectedAge()
                                                            ? 'bg-[#65e8a4] bg-opacity-20 text-[#65e8a4]'
                                                            : 'bg-red-500 bg-opacity-20 text-red-300'
                                                    }`">
                                                    Age
                                                </span>
                                                <span
                                                    :class="`rounded-full px-2 py-1 text-xs ${
                                                        getSelectedPersonality()
                                                            ? 'bg-[#65e8a4] bg-opacity-20 text-[#65e8a4]'
                                                            : 'bg-red-500 bg-opacity-20 text-red-300'
                                                    }`">
                                                    Personality
                                                </span>
                                                <span
                                                    :class="`rounded-full px-2 py-1 text-xs ${
                                                        getSelectedRelationship()
                                                            ? 'bg-[#65e8a4] bg-opacity-20 text-[#65e8a4]'
                                                            : 'bg-red-500 bg-opacity-20 text-red-300'
                                                    }`">
                                                    Relationship
                                                </span>
                                                <span
                                                    :class="`rounded-full px-2 py-1 text-xs ${
                                                        getSelectedHobbies().length > 0
                                                            ? 'bg-[#65e8a4] bg-opacity-20 text-[#65e8a4]'
                                                            : 'bg-red-500 bg-opacity-20 text-red-300'
                                                    }`">
                                                    Hobbies
                                                </span>
                                                <span
                                                    :class="`rounded-full px-2 py-1 text-xs ${
                                                        getSelectedLocation()
                                                            ? 'bg-[#65e8a4] bg-opacity-20 text-[#65e8a4]'
                                                            : 'bg-red-500 bg-opacity-20 text-red-300'
                                                    }`">
                                                    Location
                                                </span>
                                                <span
                                                    :class="`rounded-full px-2 py-1 text-xs ${
                                                        getSelectedOccupation()
                                                            ? 'bg-[#65e8a4] bg-opacity-20 text-[#65e8a4]'
                                                            : 'bg-red-500 bg-opacity-20 text-red-300'
                                                    }`">
                                                    Occupation
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Success View - Matches React PostCreationSuccess -->
                <div
                    v-else
                    class="flex min-h-[600px] flex-col items-center justify-center bg-black p-6 text-white">
                    <!-- Sent Success View -->
                    <template v-if="isSentSuccess">
                        <div
                            class="mb-6 flex h-16 w-16 items-center justify-center rounded-full bg-[#65e8a4]">
                            <font-awesome-icon :icon="faCheck" class="h-8 w-8 text-black" />
                        </div>
                        <h2 class="mb-3 text-2xl font-bold">Successfully Sent!</h2>
                        <p class="mb-5 max-w-md text-center text-gray-300">
                            Your fantasy has been sent to {{ modelDisplayName }}. You'll receive a
                            notification when they respond.
                        </p>
                        <div class="mt-4 text-sm text-gray-400">Redirecting to home...</div>
                    </template>

                    <!-- Default Success View -->
                    <template v-else>
                        <div
                            class="mb-6 flex h-16 w-16 items-center justify-center rounded-full bg-[#65e8a4]">
                            <font-awesome-icon :icon="faCheck" class="h-8 w-8 text-black" />
                        </div>
                        <h1 class="mb-3 text-center text-4xl font-bold">
                            {{ modelDisplayName }} Wants to Read This
                        </h1>
                        <p class="mb-10 text-center text-lg text-gray-300">
                            Send your fantasy and get a personal response — completely free
                        </p>

                        <!-- DM Button (only show if both fantasy_is_active and fantasy_as_dm are enabled) -->
                        <template
                            v-if="
                                selectedModel &&
                                selectedModel.fantasy_is_active &&
                                selectedModel.fantasy_as_dm
                            ">
                            <button
                                @click="handleSendToCreator"
                                class="mb-4 flex w-full max-w-lg items-center justify-center rounded-lg bg-[#65e8a4] px-6 py-4 font-semibold text-black transition-all hover:opacity-90">
                                <font-awesome-icon :icon="faPaperPlaneTop" class="mr-2 h-5 w-5" />
                                DM {{ modelDisplayName }}
                            </button>

                            <p class="mb-8 text-sm text-gray-400">--- OR ---</p>
                        </template>

                        <!-- Action Buttons -->
                        <div class="mb-8 grid w-full max-w-lg grid-cols-2 gap-4">
                            <button
                                @click="handleStartChat"
                                class="flex items-center justify-center gap-2 rounded-lg bg-[#1e1e1e] px-4 py-3 font-medium text-white transition-all hover:bg-[#2a2a2a]">
                                <font-awesome-icon :icon="faCommentDots" class="h-5 w-5" />
                                <span>Start Chat</span>
                            </button>
                            <button
                                @click="handleShare"
                                class="flex items-center justify-center gap-2 rounded-lg bg-[#1e1e1e] px-4 py-3 font-medium text-white transition-all hover:bg-[#2a2a2a]">
                                <font-awesome-icon :icon="faShare" class="h-5 w-5" />
                                <span>Share Creation</span>
                            </button>
                        </div>

                        <!-- Saved Message -->
                        <div
                            class="flex w-full max-w-lg items-center justify-center rounded-lg px-6 py-3">
                            <font-awesome-icon
                                :icon="faCheck"
                                class="mr-2 h-5 w-5 text-[#65e8a4]" />
                            <span class="text-gray-300">Fantasy saved to your profile</span>
                        </div>
                    </template>
                </div>
            </div>

            <!-- Send to model modal -->
            <send-to-model-modal
                v-if="showSendToModelModal"
                :creator="selectedModel"
                @cancel="showSendToModelModal = false"
                @success="handleSendSuccess"
                :fantasy-id="createdFantasy?.id" />

            <!-- Share Modal - Matches React version -->
            <div
                v-if="showShareModal"
                class="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-70"
                @click="showShareModal = false">
                <div
                    class="relative w-full max-w-md rounded-xl bg-[#1a1a1a] p-6 shadow-xl"
                    @click.stop>
                    <!-- Header with Close Button -->
                    <div class="mb-6 flex items-center justify-between">
                        <h2 class="text-2xl font-bold text-white">Share</h2>
                        <div class="flex items-center space-x-2">
                            <button
                                @click="showShareModal = false"
                                class="rounded-full p-2 transition-colors hover:bg-[#2a2a2a]">
                                <font-awesome-icon :icon="faClose" class="h-6 w-6 text-white" />
                            </button>
                        </div>
                    </div>

                    <!-- Social Share Buttons Grid -->
                    <div class="mb-6 grid grid-cols-5 gap-4">
                        <!-- SMS -->
                        <button @click="shareViaSMS" class="flex flex-col items-center">
                            <div
                                class="mb-1 flex h-12 w-12 items-center justify-center rounded-full bg-[#4CAF50]">
                                <span class="text-xs font-bold text-white">SMS</span>
                            </div>
                            <span class="text-xs text-white">SMS</span>
                        </button>

                        <!-- Twitter/X -->
                        <button @click="shareOnTwitter" class="flex flex-col items-center">
                            <div
                                class="mb-1 flex h-12 w-12 items-center justify-center rounded-full bg-black">
                                <span class="text-lg font-bold text-white">𝕏</span>
                            </div>
                            <span class="text-xs text-white">X</span>
                        </button>

                        <!-- Reddit -->
                        <button @click="shareOnReddit" class="flex flex-col items-center">
                            <div
                                class="mb-1 flex h-12 w-12 items-center justify-center rounded-full bg-[#FF4500]">
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    viewBox="0 0 24 24"
                                    fill="white"
                                    class="h-6 w-6">
                                    <path
                                        fill="white"
                                        d="M17.25 12a1.5 1.5 0 11-3 0 1.5 1.5 0 013 0zM9.75 12a1.5 1.5 0 11-3 0 1.5 1.5 0 013 0zM12 18a5.25 5.25 0 01-4.73-3h9.46A5.25 5.25 0 0112 18z" />
                                </svg>
                            </div>
                            <span class="text-xs text-white">Reddit</span>
                        </button>

                        <!-- WhatsApp -->
                        <button @click="shareOnWhatsApp" class="flex flex-col items-center">
                            <div
                                class="mb-1 flex h-12 w-12 items-center justify-center rounded-full bg-[#25D366]">
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    viewBox="0 0 24 24"
                                    fill="white"
                                    class="h-6 w-6">
                                    <path
                                        d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
                                </svg>
                            </div>
                            <span class="text-xs text-white">WhatsApp</span>
                        </button>

                        <!-- Telegram -->
                        <button @click="shareOnTelegram" class="flex flex-col items-center">
                            <div
                                class="mb-1 flex h-12 w-12 items-center justify-center rounded-full bg-[#0088cc]">
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    viewBox="0 0 24 24"
                                    fill="white"
                                    class="h-6 w-6">
                                    <path
                                        d="M12 0C5.373 0 0 5.373 0 12s5.373 12 12 12 12-5.373 12-12S18.627 0 12 0zm5.562 8.248l-1.97 9.269c-.145.658-.537.818-1.084.51l-3-2.211-1.447 1.394c-.16.16-.295.295-.605.295l.213-3.053 5.56-5.023c.242-.213-.054-.333-.373-.121l-6.871 4.326-2.962-.924c-.643-.204-.657-.643.136-.953l11.59-4.448c.538-.196 1.006.128.813.939z" />
                                </svg>
                            </div>
                            <span class="text-xs text-white">Telegram</span>
                        </button>
                    </div>

                    <!-- URL Input with Copy Button -->
                    <div class="relative">
                        <div class="flex items-center overflow-hidden rounded-lg bg-[#2a2a2a]">
                            <input
                                type="text"
                                :value="redirectUrl"
                                readonly
                                class="flex-1 border-none bg-transparent px-4 py-3 text-white outline-none" />
                            <button
                                @click="copyToClipboard"
                                class="flex items-center bg-[#333] px-4 py-3 text-white transition-colors hover:bg-[#444]">
                                <font-awesome-icon
                                    :icon="copied ? faCheck : faCopy"
                                    class="h-5 w-5" />
                            </button>
                        </div>
                        <div
                            v-if="copied"
                            class="absolute -top-8 right-0 rounded bg-[#65e8a4] px-2 py-1 text-xs text-black">
                            Copied to clipboard!
                        </div>
                    </div>
                </div>
            </div>
            <PlatformSubscriptionModal
                :is-open="subscriptionModalVisible"
                @close="subscriptionModalVisible = false" />
            <TokenPurchaseModal
                :show="isTokenModalOpen"
                :isLogged="!!user"
                @close="isTokenModalOpen = false" />
            <!-- Error toast -->
            <Transition
                enter-active-class="transition-opacity duration-300 ease-out"
                enter-from-class="opacity-0"
                enter-to-class="opacity-100"
                leave-active-class="transition-opacity duration-200 ease-in"
                leave-from-class="opacity-100"
                leave-to-class="opacity-0">
                <div
                    v-if="errorToast"
                    class="fixed bottom-4 right-4 z-50 flex items-center rounded-lg bg-red-600 px-4 py-3 text-white shadow-lg">
                    <FontAwesomeIcon :icon="['fas', 'exclamation-circle']" class="mr-2 h-5 w-5" />
                    <span>{{ errorToast }}</span>
                    <button @click="errorToast = null" class="ml-4 text-white hover:text-gray-200">
                        <FontAwesomeIcon :icon="faClose" class="h-4 w-4" />
                    </button>
                </div>
            </Transition>
        </div>
    </Teleport>
</template>
